// The lights of a room (the Lighting tools). Only their parameters are stored
// on the room (`ProductInstance.lights`); where each one is and where it shines
// are derived here, from the benches, backrests, walls and ceiling it is fixed
// to, so it follows them. Pure: the plan, the sections, the RCP view, 3D and the
// Lighting table all read it. Every point is in the room's LOCAL frame.

import type {
  Backrest,
  BackrestCut,
  BenchProfilePoint,
  ChannelAnchor,
  Lang,
  ProductInstance,
  ProductTemplate,
  RoomLight,
  StripHost,
  StripPos,
  Vec2,
} from '../shared/types';
import { BUILTIN_LIGHTING } from '../shared/builtinProducts';
import { productName } from '../shared/names';
import { unreachable } from '../shared/exhaustive';
import {
  type FibreKit,
  isFibreKit,
} from './lightLife';
import type { DxfEntity } from '../shared/dxf';
import {
  A1_CHANNEL,
  type BenchLine,
  frontTakesChannel,
  roomBenchLines,
} from './benchSolids';
import {
  backrestBoards,
  backrestCorners,
  backrestFigures,
  backrestRun,
  backrestSpacerBand,
  endStation,
  facePoint,
  pathOutsideCuts,
} from './backrest';
import {
  profileRing,
  profileSeatHeight,
} from './benchProfile';
import { polygonCentroid } from './flooring';
import {
  activePiece,
  ceilingShapeOf,
  hasCeiling,
  insidePoint,
  isFlatCeiling,
  outerAt,
  resolveCeiling,
  surfaceNormal,
  underAt,
} from './ceiling';
import {
  ceilingThicknessOf,
  heaterWallTop,
  innerWallBase,
  interiorHeight,
  partSpan,
  roomBase,
} from './heights';
import {
  faceRadius,
  heaterWallBands,
  type PolyEdge,
  polyEdges,
  polyInnerCarved,
  polyInnerWallBand,
  RADIUS_EPS,
  resolvedOutline,
  roomCorners,
  wallView,
} from './polyRoom';
import {
  contentMemo,
  HEAVY_FIELDS,
} from './plainData';
import { wallThicknessOf } from './dent';
import {
  arcPoints,
  pointInPolygon,
  projectToSegment,
  toInstanceLocal,
} from './geometry';

export type StripLight = Extract<RoomLight, { kind: 'strip' }>;

/** How far a strip stops short of each end it does not join another at (mm). */
export const STRIP_INSET = 50;
/** How far behind its front board an A1 strip's bench takes its skirting (mm). */
export const A1_SKIRTING_SET_BACK = 30;
/** A strip's plan width and height (mm): what the plan, a section and 3D draw. */
export const STRIP_WIDTH = 14;
export const STRIP_HEIGHT = 8;
/** The lights' colour on the plan and its symbols, and its DXF colour index. */
export const LIGHT_AMBER = '#ffb020';
export const LIGHT_ACI = 40;
/** A light fixture's colour where it is cut or seen; its beam's, in a section. */
export const LIGHT_FIXTURE_COLOR = '#ffe066';
export const LIGHT_BEAM_RGB = '255, 228, 0';
/** The strip positions, in the order the tool's options list them. */
export const STRIP_POSITIONS: readonly StripPos[] = ['A1', 'A2', 'B', 'C', 'D'];
/** Which way each position shines — the Strip tools are one per way: down A1
 *  (under a bench's front), A2 (at its skirting's foot), D (under a backrest's
 *  boards); up B (behind a seat), C (over a backrest's boards). */
export const STRIP_FACING: Record<StripPos, 'up' | 'down'> = { A1: 'down', A2: 'down', B: 'up', C: 'up', D: 'down' };

/** Where a light shines: up or down, leaning `tilt` degrees from the vertical
 *  toward the plan direction it is carried with (into the room, or off a wall). */
export interface Shine { up: boolean; tilt: number }

/** A strip's place on its host: `d` mm from the host's wall-side face (a
 *  bench's slat frame, a backrest's face), the strip's middle at `z` mm. */
/** Where a strip sits: `d` from its host's wall-side face, `z` its middle — or,
 *  in an A1 channel (`channel`, `A1_CHANNEL`), its lens, the channel over it. */
export interface StripPlace extends Shine { d: number; z: number; channel?: boolean }

export function lightsOf(inst: ProductInstance): RoomLight[] {
  return inst.lights ?? [];
}

export function stripsOf(inst: ProductInstance): StripLight[] {
  return lightsOf(inst).filter((l): l is StripLight => l.kind === 'strip');
}

// ---- A steam profile's places -------------------------------------------------

/** The notch every steam profile has under its front overhang (30 × 20 mm on
 *  Stenal's): its middle, width and height — the front-most pocket that opens
 *  downward —, or null. */
export function profileNotch(profile: readonly BenchProfilePoint[]): { x: number; y: number; w: number; h: number } | null {
  const ring = profileRing(profile);
  const n = ring.length;
  let best: { x: number; y: number; w: number; h: number } | null = null;
  for (let i = 0; i < n; i++) {
    for (const dir of [1, -1]) {
      const p0 = ring[i], p1 = ring[(i + dir + n) % n], p2 = ring[(i + 2 * dir + 2 * n) % n], p3 = ring[(i + 3 * dir + 3 * n) % n];
      const h = p1.y - p0.y, w = Math.abs(p2.x - p1.x);
      const vertical = (a: Vec2, b: Vec2): boolean => Math.abs(a.x - b.x) < 0.5;
      if (!vertical(p0, p1) || !vertical(p2, p3) || Math.abs(p1.y - p2.y) > 0.5 || Math.abs(p3.y - p0.y) > 0.5) continue;
      if (h < 8 || h > 60 || w < 12 || w > 80 || Math.abs(p2.y - p3.y - h) > 0.5) continue;
      // It opens downward: just under the pocket's roof is outside the profile,
      // just beside it inside.
      const mid = { x: (p1.x + p2.x) / 2, y: p0.y + h / 2 };
      const ringPts = ring.map((q) => ({ x: q.x, y: q.y }));
      if (pointInPolygon(mid, ringPts) || !pointInPolygon({ x: Math.min(p1.x, p2.x) - 2, y: mid.y }, ringPts)) continue;
      if (!best || mid.x > best.x) best = { x: mid.x, y: mid.y, w, h };
    }
  }
  return best;
}

/** Where a horizontal line at `y` crosses the profile: its x intervals, inside. */
function profileXsAt(profile: readonly BenchProfilePoint[], y: number): Array<[number, number]> {
  const ring = profileRing(profile);
  const xs: number[] = [];
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i], b = ring[(i + 1) % ring.length];
    if ((a.y > y) === (b.y > y)) continue;
    xs.push(a.x + ((y - a.y) / (b.y - a.y)) * (b.x - a.x));
  }
  xs.sort((p, q) => p - q);
  const out: Array<[number, number]> = [];
  for (let k = 0; k + 1 < xs.length; k += 2) out.push([xs[k], xs[k + 1]]);
  return out;
}

/** A steam profile's own backrest, when it has one: rising well above its seat. */
function profileBackrest(profile: readonly BenchProfilePoint[]): { top: number; x0: number; x1: number } | null {
  const ring = profileRing(profile);
  const top = Math.max(...ring.map((p) => p.y));
  if (top < profileSeatHeight(profile) + 200) return null;
  const xs = ring.filter((p) => p.y >= top - 1).map((p) => p.x);
  return { top, x0: Math.min(...xs), x1: Math.max(...xs) };
}

/** A steam bench's places for each position (its profile's own frame, unlifted). */
function profilePlace(profile: readonly BenchProfilePoint[], pos: StripPos): StripPlace | null {
  const ring = profileRing(profile);
  switch (pos) {
    case 'A1': {
      const n = profileNotch(profile);
      return n ? { d: n.x, z: n.y, up: false, tilt: 5 } : null;
    }
    case 'A2': {
      const floor = Math.min(...ring.map((p) => p.y));
      const x0 = Math.max(...ring.filter((p) => p.y <= floor + 0.5).map((p) => p.x));
      return { d: x0 + 8, z: floor + 12, up: false, tilt: 50 };
    }
    case 'B': {
      const seat = profileSeatHeight(profile);
      const near = ring.filter((p) => p.y >= seat - 30 && p.y <= seat + 10);
      if (!near.length) return null;
      const back = near.reduce((a, b) => (b.x < a.x ? b : a));
      return { d: back.x + 10, z: back.y + 4, up: true, tilt: 5 };
    }
    case 'C': {
      const br = profileBackrest(profile);
      return br ? { d: (br.x0 + br.x1) / 2, z: br.top + 4, up: true, tilt: 5 } : null;
    }
    case 'D': {
      const br = profileBackrest(profile);
      if (!br) return null;
      const z = br.top - 20;
      const back = profileXsAt(profile, z).find(([a]) => a <= br.x0 + 1);
      return back ? { d: back[1] + 6, z, up: false, tilt: 5 } : null;
    }
  }
}

// ---- Where a strip sits ---------------------------------------------------------

/**
 * Where a strip of position `pos` sits on a bench, by the bench's line — or null
 * where the bench has no such place. A slatted bench: A1 behind its front board,
 * 12 mm in, its middle 11 mm over the board's lower edge, shining down and 10°
 * into the room; A2 in front of the skirting's foot (set back or not), on the
 * floor or the seat below, down and 50° out onto it; B on the seat's back edge,
 * up. A steam bench: A1 in its notch, A2 at its base's front, B at its seat's
 * back, C / D on its own backrest. A plain block takes A1 under (or on) its
 * front edge.
 */
export function benchStripPlace(line: BenchLine, pos: StripPos): StripPlace | null {
  if (line.profile) {
    const p = profilePlace(line.profile, pos);
    return p ? { ...p, z: p.z + (line.lift ?? 0) } : null;
  }
  const front = line.front;
  switch (pos) {
    case 'A1':
      // A front board hanging below the seat takes it in the channel carved into
      // it and the closure board behind it, the lens up the groove, straight down.
      if (front?.channel) {
        const G = A1_CHANNEL.groove;
        return { d: front.d + G.intoFront - G.width / 2, z: line.top - front.hang + G.height - A1_CHANNEL.height, up: false, tilt: 0, channel: true };
      }
      if (front) return { d: front.d - 12, z: line.top - front.hang + 11, up: false, tilt: 10 };
      return line.slab != null
        ? { d: line.depth - 20, z: line.top - line.slab - 6, up: false, tilt: 10 }
        : { d: line.depth + 7, z: line.top - 20, up: false, tilt: 10 };
    case 'A2':
      return { d: (line.skirting?.d ?? front?.d ?? line.depth) + 8, z: (line.skirting?.base ?? 0) + 12, up: false, tilt: 50 };
    case 'B':
      return { d: 10, z: line.top + 4, up: true, tilt: 5 };
    case 'C':
    case 'D':
      return null;
  }
}

/** Where a strip sits on a backrest: C on its spacers' top, behind its top
 *  board, D under their foot, behind its bottom board (cavalletto.dxf), both in
 *  the middle of its stand-off. */
export function backrestStripPlace(inst: ProductInstance, br: Backrest, pos: StripPos): StripPlace | null {
  if (pos !== 'C' && pos !== 'D') return null;
  const boards = backrestBoards(inst, br);
  const f = backrestFigures(br);
  const d = f.standOff / 2;
  const [s0, s1] = backrestSpacerBand(boards[0].z0, boards[boards.length - 1].z1);
  return pos === 'C'
    ? { d, z: s1 + STRIP_HEIGHT / 2, up: true, tilt: 5 }
    : { d, z: s0 - STRIP_HEIGHT / 2, up: false, tilt: 5 };
}

/** How a backrest is lit from behind: its up strip (C), its down strip (D), both
 *  or none. */
export type BackrestLighting = 'both' | 'down' | 'none' | 'up';

function backrestStrips(inst: ProductInstance, id: string): StripLight[] {
  return stripsOf(inst).filter((l) => 'backrest' in l.host && l.host.backrest === id && (l.pos === 'C' || l.pos === 'D'));
}

export function backrestLighting(inst: ProductInstance, id: string): BackrestLighting {
  const pos = new Set(backrestStrips(inst, id).map((l) => l.pos));
  return pos.has('C') ? (pos.has('D') ? 'both' : 'up') : pos.has('D') ? 'down' : 'none';
}

/** Light a backrest from behind as asked: the strip it lacks added — the other's
 *  product and colour taken, so the two match —, the one it no longer wants
 *  removed. The ids of the strips added. */
export function setBackrestLighting(inst: ProductInstance, id: string, want: BackrestLighting, newId: () => string): string[] {
  const has = backrestStrips(inst, id);
  const wanted = new Set<StripPos>(want === 'both' ? ['C', 'D'] : want === 'up' ? ['C'] : want === 'down' ? ['D'] : []);
  const gone = new Set(has.filter((l) => !wanted.has(l.pos)).map((l) => l.id));
  if (gone.size) inst.lights = (inst.lights ?? []).filter((l) => !gone.has(l.id));
  const kept = has.find((l) => !gone.has(l.id));
  const added: string[] = [];
  for (const pos of wanted) {
    if (has.some((l) => l.pos === pos)) continue;
    const light: StripLight = {
      id: newId(), kind: 'strip', pos, host: { backrest: id },
      ...(kept?.productId ? { productId: kept.productId } : {}), ...(kept?.productName ? { productName: kept.productName } : {}), ...(kept?.productNameI18n ? { productNameI18n: { ...kept.productNameI18n } } : {}), ...(kept?.colour ? { colour: kept.colour } : {}),
    };
    (inst.lights ??= []).push(light);
    added.push(light.id);
  }
  if (inst.lights && !inst.lights.length) delete inst.lights;
  return added;
}

/** The positions a host offers: a bench its A1, A2, B (and C, D on a steam
 *  bench's own backrest), a backrest its C and D. */
export function hostPositions(inst: ProductInstance, host: StripHost, lines?: BenchLine[]): StripPos[] {
  if ('backrest' in host) return inst.backrests?.some((b) => b.id === host.backrest) ? ['C', 'D'] : [];
  const line = (lines ?? roomBenchLines(inst)).find((l) => l.kind === 'band' && l.id === host.bench);
  return line ? STRIP_POSITIONS.filter((p) => benchStripPlace(line, p)) : [];
}

/** One strip's full line on its host (no insets yet): the plan path at its
 *  depth, its height, the way it shines, which side of its path the room is on
 *  and its host's plan footprint (what a join at a corner may run into). */
export interface StripLine {
  light: StripLight;
  pts: Vec2[];
  z: number;
  shine: Shine;
  /** +1: the room (where it leans) is left of its path, −1: right. */
  side: 1 | -1;
  /** Its host's plan footprint: the bench band, the backrest's boards and stand-off. */
  foot: Vec2[];
  /** How far it is from its host's wall-side face (mm). */
  d: number;
  /** In an A1 channel: `z` its lens (`StripPlace.channel`). */
  channel?: boolean;
}

const unit = (v: Vec2): Vec2 => { const L = Math.hypot(v.x, v.y) || 1; return { x: v.x / L, y: v.y / L }; };
const sideOf = (a: Vec2, b: Vec2, toward: Vec2): 1 | -1 => ((b.x - a.x) * toward.y - (b.y - a.y) * toward.x >= 0 ? 1 : -1);

/** A bench line's plan footprint: its wall-side face to its front. */
function benchFoot(line: BenchLine): Vec2[] {
  const back = line.path(0), front = line.path(line.depth);
  return back.length >= 2 && front.length >= 2 ? [...back, ...[...front].reverse()] : [];
}

/** The full line of one strip on its host, or null where its host or its
 *  position is gone. */
export function stripLine(inst: ProductInstance, light: StripLight, lines?: BenchLine[]): StripLine | null {
  const host = light.host;
  if ('backrest' in host) {
    const br = inst.backrests?.find((b) => b.id === host.backrest);
    const run = br ? backrestRun(inst, br) : null;
    const place = br ? backrestStripPlace(inst, br, light.pos) : null;
    if (!br || !run || !place) return null;
    const f = backrestFigures(br);
    const at = (k: 0 | 1, d: number): Vec2 => facePoint(run.face, endStation(run.face, run.ends[k], d), d);
    const a = at(0, place.d), b = at(1, place.d);
    const deep = f.standOff + f.boardThickness;
    return {
      light, pts: [a, b], z: place.z, shine: { up: place.up, tilt: place.tilt }, side: sideOf(a, b, run.face.n), d: place.d,
      foot: [at(0, 0), at(1, 0), at(1, deep), at(0, deep)],
    };
  }
  const line = (lines ?? roomBenchLines(inst)).find((l) => l.kind === 'band' && l.id === host.bench);
  const place = line ? benchStripPlace(line, light.pos) : null;
  if (!line || !place) return null;
  const pts = line.path(place.d);
  if (pts.length < 2) return null;
  const deeper = line.path(place.d + 1)[0];
  const toward = { x: deeper.x - pts[0].x, y: deeper.y - pts[0].y };
  return { light, pts, z: place.z, shine: { up: place.up, tilt: place.tilt }, side: sideOf(pts[0], pts[1], toward), foot: benchFoot(line), d: place.d, ...(place.channel ? { channel: true } : {}) };
}

// ---- Strips joined into runs ------------------------------------------------------

/** A run of strips as lit: the strips of one position and product joined end to
 *  end at their corners into one path, stopping short of its free ends. */
export interface StripRun {
  /** The strips it is made of, in path order. */
  ids: string[];
  pos: StripPos;
  productId?: string;
  pts: Vec2[];
  /** +1: it leans to the left of its path, −1: to the right. */
  side: 1 | -1;
  z: number;
  shine: Shine;
  closed: boolean;
  /** Its length (mm). */
  length: number;
  /** Each strip's own part of `pts`, in path order: what is drawn, picked and
   *  outlined as that strip. */
  pieces: Array<{ id: string; pts: Vec2[] }>;
  /** What it is made of as LEDs, in path order (the electrical schematic's boxes):
   *  each strip's own stretch (`ids` its one), each rounded corner's arc between
   *  two strips its own (`corner`, `ids` the two it joins); a sharp corner none. */
  segments: StripSegment[];
  /** Its free ends, the start's then the end's (none on a loop). */
  free: StripFreeEnd[];
  /** Its strips sit in A1 channels: `z` their lens. */
  channel?: boolean;
  /** Up (C, +1) and down (D, −1) strips on the same backrests lie on one line:
   *  the plan draws and picks them `STRIP_APART_PX` apart, C toward the room. */
  apart?: 1 | -1;
}

export interface StripSegment { ids: string[]; pts: Vec2[]; length: number; corner?: true }

/** How far apart the plan draws a backrest's up and down strips (px each way). */
export const STRIP_APART_PX = 3.5;

/** A path moved `by` mm square to it, toward the side it leans to (`side`). */
export function shiftedPath(pts: Vec2[], side: 1 | -1, by: number): Vec2[] {
  if (!by || pts.length < 2) return pts;
  const normal = (a: Vec2, b: Vec2): Vec2 => { const L = dist(a, b) || 1; return { x: (-(b.y - a.y) / L) * side, y: ((b.x - a.x) / L) * side }; };
  return pts.map((p, k) => {
    const n0 = k > 0 ? normal(pts[k - 1], p) : null, n1 = k + 1 < pts.length ? normal(p, pts[k + 1]) : null;
    const n = n0 && n1 ? unit({ x: n0.x + n1.x, y: n0.y + n1.y }) : (n0 ?? n1)!;
    const c = n0 && n1 ? Math.max(0.5, n.x * n1.x + n.y * n1.y) : 1;
    return { x: p.x + (n.x * by) / c, y: p.y + (n.y * by) / c };
  });
}

/** A run's free end: the strip whose field sets how far short of it the run
 *  stops, that inset, the most it may take — the run kept `STRIP_MIN_RUN` long
 *  past the other end's —, and the run's whole path from this end, untrimmed. */
export interface StripFreeEnd { id: string; field: 'insetStart' | 'insetEnd'; inset: number; max: number; path: Vec2[] }

/** The shortest a run is left by its insets (mm). */
export const STRIP_MIN_RUN = 100;

/** How far along a free end's path (from that end) a point's foot on it lies,
 *  kept within the inset that end may take: the inset a drag to it sets. */
export function stripInsetAt(end: StripFreeEnd, p: Vec2): number {
  let best = Infinity, at = 0, s = 0;
  for (let k = 1; k < end.path.length; k++) {
    const a = end.path[k - 1], b = end.path[k], len = dist(a, b);
    if (len < 1e-9) continue;
    const t = Math.max(0, Math.min(len, ((p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y)) / len));
    const d = dist(p, { x: a.x + ((b.x - a.x) * t) / len, y: a.y + ((b.y - a.y) * t) / len });
    if (d < best) { best = d; at = s + t; }
    s += len;
  }
  return Math.max(0, Math.min(end.max, at));
}

const dist = (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.y - b.y);

/** Where the infinite lines through a→b and c→d cross, or null when parallel. */
function crossing(a: Vec2, b: Vec2, c: Vec2, d: Vec2): Vec2 | null {
  const r = { x: b.x - a.x, y: b.y - a.y }, s = { x: d.x - c.x, y: d.y - c.y };
  const den = r.x * s.y - r.y * s.x;
  if (Math.abs(den) < 1e-9 * Math.hypot(r.x, r.y) * Math.hypot(s.x, s.y)) return null;
  const t = ((c.x - a.x) * s.y - (c.y - a.y) * s.x) / den;
  return { x: a.x + r.x * t, y: a.y + r.y * t };
}

/** How far a point lies outside a polygon (0 inside or on it). */
function outside(p: Vec2, poly: Vec2[]): number {
  if (poly.length < 3 || pointInPolygon(p, poly)) return 0;
  let best = Infinity;
  for (let k = 0; k < poly.length; k++) {
    const a = poly[k], b = poly[(k + 1) % poly.length];
    const L2 = (b.x - a.x) ** 2 + (b.y - a.y) ** 2;
    const t = L2 > 0 ? Math.max(0, Math.min(1, ((p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y)) / L2)) : 0;
    best = Math.min(best, Math.hypot(p.x - (a.x + (b.x - a.x) * t), p.y - (a.y + (b.y - a.y) * t)));
  }
  return best;
}

/** A path cut `a` mm short of its start and `b` short of its end (an end
 *  shorter than that keeps nothing). */
export function trimPath(pts: Vec2[], a: number, b: number): Vec2[] {
  const cut = (p: Vec2[], m: number): Vec2[] => {
    let left = m;
    for (let i = 1; i < p.length; i++) {
      const L = dist(p[i - 1], p[i]);
      if (left < L) {
        const t = left / L;
        return [{ x: p[i - 1].x + (p[i].x - p[i - 1].x) * t, y: p[i - 1].y + (p[i].y - p[i - 1].y) * t }, ...p.slice(i)];
      }
      left -= L;
    }
    return [];
  };
  const head = a > 0 ? cut(pts, a) : pts;
  if (head.length < 2) return [];
  const tail = b > 0 ? cut([...head].reverse(), b).reverse() : head;
  return tail.length >= 2 ? tail : [];
}

const pathLength = (pts: Vec2[]): number => pts.reduce((s, p, i) => (i ? s + dist(pts[i - 1], p) : 0), 0);

/** A path without its repeated points. */
function dedupe(pts: Vec2[]): Vec2[] {
  return pts.filter((p, i) => i === 0 || dist(p, pts[i - 1]) > 0.01);
}

// ---- A room's lighting, resolved once ----------------------------------------------

/** What a room's lighting is resolved from: the room — its lights and all they
 *  are fixed to (benches, backrests, walls, partitions, its ceiling) — and the
 *  products its lights name (the memo leaves the heavy fields out). */
function lightingInput(room: ProductInstance): unknown {
  return [room, lightsOf(room).map((l) => lightProduct(l) ?? null)];
}

/** A room's lighting as resolved: its strip runs, its emitters and its light
 *  levels (each under its beam sizes and cell), kept while what it is resolved
 *  from holds. */
interface ResolvedLighting {
  runs?: StripRun[];
  emitters: Map<string, Emitter[]>;
  levels: Map<string, LightLevels | null>;
}
const RESOLVED_LIGHTING = contentMemo<ResolvedLighting>({ max: 500, skip: HEAVY_FIELDS });

/** The ONE cache of a room's lighting: the plan, a section, the RCP, the 3D
 *  view, the light levels, the hit tests and the Lighting table ask for a room's
 *  runs and emitters many times a frame, and every one of them gets the same
 *  answer until something it is resolved from changes (`lightingInput`). What it
 *  hands out is shared: never mutate it. */
function resolvedLighting(room: ProductInstance): ResolvedLighting {
  return RESOLVED_LIGHTING.get(room.id, () => lightingInput(room), () => ({ emitters: new Map(), levels: new Map() }));
}

/**
 * Every strip of a room joined into runs. Two strips of one position and one
 * product, at one height, join where their lines meet at a room corner — a
 * mitre's shared end, a rounded corner piece's arc at their depth between them,
 * or the corner their lines cross at when one bench butts the other, so long as
 * neither runs past its own host except into the other's — and the run stops
 * `insetStart` / `insetEnd` short only of its free ends. A strip whose host is
 * gone gives none. Resolved once per room (`resolvedLighting`), unless handed its
 * bench lines (`given`, a tool's preview).
 */
export function stripRuns(inst: ProductInstance, given?: BenchLine[]): StripRun[] {
  if (!stripsOf(inst).length) return [];
  if (given) return stripRunsOf(inst, given);
  const r = resolvedLighting(inst);
  if (!r.runs) r.runs = stripRunsOf(inst, roomBenchLines(inst));
  return r.runs;
}

function stripRunsOf(inst: ProductInstance, lines: BenchLine[]): StripRun[] {
  const strips = stripsOf(inst);
  const raw = strips.map((l) => stripLine(inst, l, lines)).filter((x): x is StripLine => !!x);
  const pts = raw.map((r) => r.pts.map((p) => ({ ...p })));
  // links[i][e] = [j, f]: end e (0 start, 1 end) of line i joins end f of line j.
  const links: Array<[[number, number] | null, [number, number] | null]> = raw.map(() => [null, null]);
  const same = (i: number, j: number): boolean => raw[i].light.pos === raw[j].light.pos
    && (raw[i].light.productId ?? '') === (raw[j].light.productId ?? '') && Math.abs(raw[i].z - raw[j].z) < 1;
  const endPt = (i: number, e: number): Vec2 => (e ? pts[i][pts[i].length - 1] : pts[i][0]);
  const link = (i: number, e: number, j: number, f: number): void => { links[i][e] = [j, f]; links[j][f] = [i, e]; };
  // arcs[i][e]: the rounded corner's arc hung on end e of line i (its own points
  // in pts[i]), whole from end to end in pts[i]'s direction, and the other line.
  const arcs: Array<[{ pts: Vec2[]; other: number } | null, { pts: Vec2[]; other: number } | null]> = raw.map(() => [null, null]);
  const corners = lines.filter((l) => l.kind === 'corner');
  const backCorners = raw.some((r) => 'backrest' in r.light.host) ? backrestCorners(inst) : [];
  /** The arc of a rounded corner piece — a bench's at this position's depth, a
   *  backrest's at the strip's — from end a to end b (its own ends left out), or
   *  null when no piece lies between them. */
  const arcBetween = (r: StripLine, a: Vec2, b: Vec2): Vec2[] | null => {
    const arcs = 'backrest' in r.light.host
      ? backCorners.map((c) => dedupe(c.path(r.d)))
      : corners.map((c) => { const place = benchStripPlace(c, r.light.pos); return place ? dedupe(c.path(place.d)) : []; });
    for (const arc of arcs) {
      if (arc.length < 2) continue;
      const s = arc[0], t = arc[arc.length - 1];
      if (dist(s, a) < 2 && dist(t, b) < 2) return arc.slice(1, -1);
      if (dist(t, a) < 2 && dist(s, b) < 2) return [...arc].reverse().slice(1, -1);
    }
    return null;
  };
  for (let i = 0; i < raw.length; i++) {
    for (let j = i + 1; j < raw.length; j++) {
      if (!same(i, j)) continue;
      for (const e of [0, 1]) for (const f of [0, 1]) {
        if (links[i][e] || links[j][f]) continue;
        const a = endPt(i, e), b = endPt(j, f);
        if (dist(a, b) < 2) { link(i, e, j, f); continue; }
        const arc = arcBetween(raw[i], a, b);
        if (arc) {
          if (e === 1) pts[i].push(...arc); else pts[i].unshift(...[...arc].reverse());
          arcs[i][e] = { pts: e === 1 ? [a, ...arc, b] : [b, ...[...arc].reverse(), a], other: j };
          link(i, e, j, f);
          continue;
        }
        // A sharp corner: the two lines meet where they cross — a strip may run
        // past its own end only into the other's host, and a corner farther
        // from an end than the other host is deep is a T, not a corner.
        const ai = pts[i], aj = pts[j];
        const iA = e ? ai[ai.length - 2] : ai[1], jA = f ? aj[aj.length - 2] : aj[1];
        const x = crossing(iA, a, jA, b);
        if (!x) continue;
        const past = (end: Vec2, inner: Vec2): boolean => (x.x - end.x) * (end.x - inner.x) + (x.y - end.y) * (end.y - inner.y) > 2 * dist(end, inner);
        const reach = (k: number): number => {
          const foot = raw[k].foot, h = foot.length / 2;
          return Math.max(raw[k].d, foot.length >= 4 ? Math.max(dist(foot[0], foot[foot.length - 1]), dist(foot[h - 1], foot[h])) : 0);
        };
        if (past(a, iA) && outside(a, raw[j].foot) > 2) continue;
        if (past(b, jA) && outside(b, raw[i].foot) > 2) continue;
        if (dist(a, x) > reach(j) + 2 || dist(b, x) > reach(i) + 2) continue;
        if (e) ai[ai.length - 1] = x; else ai[0] = x;
        if (f) aj[aj.length - 1] = x; else aj[0] = x;
        link(i, e, j, f);
      }
    }
  }
  // Walk the chains.
  const done = new Set<number>();
  const out: StripRun[] = [];
  const chainFrom = (start: number, startEnd: number): { order: Array<[number, boolean]>; closed: boolean } => {
    const order: Array<[number, boolean]> = [];
    let i = start, rev = startEnd === 1; // entered at its start: walk forward
    for (;;) {
      order.push([i, rev]);
      done.add(i);
      const exit = rev ? 0 : 1;
      const nx = links[i][exit];
      if (!nx) return { order, closed: false };
      if (nx[0] === start) return { order, closed: true };
      if (done.has(nx[0])) return { order, closed: false };
      i = nx[0]; rev = nx[1] === 1;
    }
  };
  for (let i = 0; i < raw.length; i++) {
    if (done.has(i)) continue;
    // Start at a free end; a loop starts anywhere.
    let s = i, se = 0;
    for (let k = 0, cur = i, ce = 0; k < raw.length; k++) {
      const back = links[cur][ce];
      if (!back) { s = cur; se = ce; break; }
      cur = back[0]; ce = back[1] === 1 ? 0 : 1;
      if (cur === i) { s = i; se = 0; break; }
    }
    const { order, closed } = chainFrom(s, se);
    let path: Vec2[] = [];
    for (const [k, rev] of order) path.push(...(rev ? [...pts[k]].reverse() : pts[k]));
    path = dedupe(path);
    if (closed && path.length > 2 && dist(path[0], path[path.length - 1]) > 0.01) path.push({ ...path[0] });
    const first = raw[order[0][0]].light, last = raw[order[order.length - 1][0]].light;
    const inA = closed ? 0 : (order[0][1] ? first.insetEnd : first.insetStart) ?? STRIP_INSET;
    const inB = closed ? 0 : (order[order.length - 1][1] ? last.insetStart : last.insetEnd) ?? STRIP_INSET;
    const trimmed = trimPath(path, Math.max(0, inA), Math.max(0, inB));
    if (trimmed.length < 2) continue;
    const pieces: StripRun['pieces'] = [];
    order.forEach(([k, rev], idx) => {
      let own = dedupe(rev ? [...pts[k]].reverse() : pts[k]);
      if (idx === 0) own = trimPath(own, Math.max(0, inA), 0);
      if (idx === order.length - 1) own = trimPath(own, 0, Math.max(0, inB));
      if (own.length >= 2) pieces.push({ id: raw[k].light.id, pts: own });
    });
    // As LEDs: each line's own stretch, its arcs apart, the run's ends set in.
    const segments: StripSegment[] = [];
    const seg = (ids: string[], p: Vec2[], corner?: true): void => {
      const q = dedupe(p);
      if (q.length >= 2 && pathLength(q) > 0.01) segments.push({ ids, pts: q, length: pathLength(q), ...(corner ? { corner } : {}) });
    };
    order.forEach(([k, rev], idx) => {
      const [as, ae] = arcs[k], n = pts[k].length;
      let bare = pts[k].slice(as ? as.pts.length - 2 : 0, n - (ae ? ae.pts.length - 2 : 0));
      if (rev) bare = [...bare].reverse();
      if (idx === 0) bare = trimPath(dedupe(bare), Math.max(0, inA), 0);
      if (idx === order.length - 1) bare = trimPath(dedupe(bare), 0, Math.max(0, inB));
      const arcSeg = (x: { pts: Vec2[]; other: number } | null, flip: boolean): void => {
        if (x) seg([raw[k].light.id, raw[x.other].light.id], flip ? [...x.pts].reverse() : x.pts, true);
      };
      arcSeg(rev ? ae : as, rev);
      seg([raw[k].light.id], bare);
      arcSeg(rev ? as : ae, rev);
    });
    const r0 = raw[order[0][0]];
    const side: 1 | -1 = order[0][1] ? (r0.side === 1 ? -1 : 1) : r0.side;
    const whole = pathLength(path);
    const free: StripFreeEnd[] = closed ? [] : [
      { id: first.id, field: order[0][1] ? 'insetEnd' : 'insetStart', inset: Math.max(0, inA), max: Math.max(0, whole - Math.max(0, inB) - STRIP_MIN_RUN), path },
      { id: last.id, field: order[order.length - 1][1] ? 'insetStart' : 'insetEnd', inset: Math.max(0, inB), max: Math.max(0, whole - Math.max(0, inA) - STRIP_MIN_RUN), path: [...path].reverse() },
    ];
    out.push({
      ids: order.map(([k]) => raw[k].light.id), pos: r0.light.pos, productId: r0.light.productId,
      pts: trimmed, side, z: r0.z, shine: r0.shine, closed, length: pathLength(trimmed), pieces, segments, free, ...(r0.channel ? { channel: true } : {}),
    });
  }
  // A part standing in front of the backrests (an infrared emitter in its own
  // backrest: `backrestCuts`) interrupts the strips behind them.
  if (inst.backrestCuts?.length) out.splice(0, out.length, ...out.flatMap((r) => (r.pos === 'C' || r.pos === 'D' ? splitRunByCuts(r, inst.backrestCuts!) : [r])));
  // A backrest's up and down strips share its line: they are drawn apart.
  const backrestsOfRun = (r: StripRun): string[] => r.ids.flatMap((id) => {
    const h = strips.find((l) => l.id === id)?.host;
    return h && 'backrest' in h ? [h.backrest] : [];
  });
  for (const r of out) {
    if (r.pos !== 'C' && r.pos !== 'D') continue;
    const mine = backrestsOfRun(r);
    if (mine.length && out.some((o) => o.pos === (r.pos === 'C' ? 'D' : 'C') && backrestsOfRun(o).some((b) => mine.includes(b)))) r.apart = r.pos === 'C' ? 1 : -1;
  }
  return out;
}

/** A run as the cuts in front of its backrests leave it (`pathOutsideCuts`):
 *  one run per stretch they leave, each with its strips' parts along it, the
 *  run's free ends kept on its first and its last. The run itself where none
 *  reaches it. */
function splitRunByCuts(r: StripRun, cuts: readonly BackrestCut[]): StripRun[] {
  const parts = pathOutsideCuts(r.pts, r.z, cuts);
  if (parts.length === 1 && Math.abs(pathLength(parts[0]) - r.length) < 0.01) return [r];
  return parts.map((pts, k) => {
    const pieces = r.pieces.flatMap((p) => pathOutsideCuts(p.pts, r.z, cuts)
      .filter((q) => { const m = pathMid(q); return pointNearPath(m, pts, 0.5); })
      .map((q) => ({ id: p.id, pts: q })));
    const segments = r.segments.flatMap((sg) => pathOutsideCuts(sg.pts, r.z, cuts)
      .filter((q) => pointNearPath(pathMid(q), pts, 0.5))
      .map((q) => ({ ...sg, pts: q, length: pathLength(q) })));
    const free = [...(k === 0 && r.free[0] ? [r.free[0]] : []), ...(k === parts.length - 1 && r.free[1] ? [r.free[1]] : [])];
    return { ...r, ids: [...new Set(pieces.map((p) => p.id))], pts, length: pathLength(pts), pieces, segments, free, closed: false };
  }).filter((x) => x.pieces.length);
}

/** The point halfway along a path. */
function pathMid(pts: readonly Vec2[]): Vec2 {
  let left = pathLength(pts as Vec2[]) / 2;
  for (let i = 0; i + 1 < pts.length; i++) {
    const a = pts[i], b = pts[i + 1], L = dist(a, b);
    if (left <= L && L > 0) return { x: a.x + ((b.x - a.x) * left) / L, y: a.y + ((b.y - a.y) * left) / L };
    left -= L;
  }
  return pts[pts.length - 1];
}

/** A strip piece's line as the plan draws and picks it: a backrest's up and
 *  down strips `by` mm apart (`STRIP_APART_PX` on screen). */
export function drawnStripPts(piece: StripPiece, by: number): Vec2[] {
  return piece.run.apart ? shiftedPath(piece.pts, piece.run.side, piece.run.apart * by) : piece.pts;
}

/** A strip's plan footprint along a run's segment (room local): `STRIP_WIDTH` wide. */
export function stripSegmentQuad(a: Vec2, b: Vec2, width = STRIP_WIDTH): Vec2[] {
  const u = unit({ x: b.x - a.x, y: b.y - a.y }), h = width / 2;
  const n = { x: -u.y * h, y: u.x * h };
  return [{ x: a.x + n.x, y: a.y + n.y }, { x: b.x + n.x, y: b.y + n.y }, { x: b.x - n.x, y: b.y - n.y }, { x: a.x - n.x, y: a.y - n.y }];
}

/** The ring round a path `width` wide, mitred at its bends: a strip's outline. */
export function pathOutline(pts: Vec2[], width = STRIP_WIDTH): Vec2[] {
  const sides = pathSides(pts, width);
  return sides ? [...sides[0], ...[...sides[1]].reverse()] : [];
}

/** A path's two sides `width` apart, mitred at its bends: a channel's lines. */
export function pathSides(pts: Vec2[], width: number): [Vec2[], Vec2[]] | null {
  if (pts.length < 2) return null;
  const half = width / 2, left: Vec2[] = [], right: Vec2[] = [], last = pts.length - 1;
  // A ring (its first point repeated last) is mitred where it closes too.
  const ring = pts.length > 3 && dist(pts[0], pts[last]) < 0.01;
  pts.forEach((b, i) => {
    const a = ring && i === 0 ? pts[last - 1] : pts[Math.max(0, i - 1)], c = ring && i === last ? pts[1] : pts[Math.min(last, i + 1)];
    const u0 = unit({ x: b.x - a.x, y: b.y - a.y }), u1 = unit({ x: c.x - b.x, y: c.y - b.y });
    const n0 = { x: -u0.y, y: u0.x }, n1 = { x: -u1.y, y: u1.x };
    const m = unit({ x: n0.x + n1.x, y: n0.y + n1.y });
    const ref = i === 0 && !ring ? n1 : n0;
    const k = half / Math.max(0.3, m.x * ref.x + m.y * ref.y);
    left.push({ x: b.x + m.x * k, y: b.y + m.y * k });
    right.push({ x: b.x - m.x * k, y: b.y - m.y * k });
  });
  return [left, right];
}

/** Whether a point lies within `tol` of a path. */
export function pointNearPath(p: Vec2, pts: Vec2[], tol: number): boolean {
  return pathDistance(p, pts) <= tol;
}

/** How far a point lies from a path (mm). */
function pathDistance(p: Vec2, pts: Vec2[]): number {
  let best = Infinity;
  for (let k = 0; k + 1 < pts.length; k++) {
    const a = pts[k], b = pts[k + 1];
    const L2 = (b.x - a.x) ** 2 + (b.y - a.y) ** 2;
    const t = L2 > 0 ? Math.max(0, Math.min(1, ((p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y)) / L2)) : 0;
    best = Math.min(best, Math.hypot(p.x - (a.x + (b.x - a.x) * t), p.y - (a.y + (b.y - a.y) * t)));
  }
  return best;
}

/** One strip as drawn: its own part of its run, and the run. */
export interface StripPiece { id: string; pts: Vec2[]; run: StripRun }

export function stripPieces(inst: ProductInstance, lines?: BenchLine[]): StripPiece[] {
  return stripRuns(inst, lines).flatMap((run) => run.pieces.map((p) => ({ ...p, run })));
}

/** The strip drawn under a point (room local), within `tol` of its outline —
 *  a backrest's up and down strips `apart` mm apart, as drawn. */
export function stripAt(inst: ProductInstance, p: Vec2, tol: number, lines?: BenchLine[], apart = 0): StripPiece | null {
  let best: StripPiece | null = null, bd = Infinity;
  for (const piece of stripPieces(inst, lines)) {
    const d = pathDistance(p, drawnStripPts(piece, apart));
    if (d <= STRIP_WIDTH / 2 + tol && d < bd) { best = piece; bd = d; }
  }
  return best;
}

/** Every strip of a room as solids: a `STRIP_WIDTH` × `STRIP_HEIGHT` box along
 *  each segment of its piece, round its middle height (room local). What a
 *  section cuts or sees and 3D models. */
export function stripSolids(inst: ProductInstance, lines?: BenchLine[]): Array<{ id: string; poly: Vec2[]; base: number; top: number; part?: 'can' | 'lens' }> {
  const out: Array<{ id: string; poly: Vec2[]; base: number; top: number; part?: 'can' | 'lens' }> = [];
  for (const piece of stripPieces(inst, lines)) {
    const { z } = piece.run;
    for (let k = 0; k + 1 < piece.pts.length; k++) {
      const a = piece.pts[k], b = piece.pts[k + 1];
      if (piece.run.channel) {
        // An A1 channel: the aluminium profile over its lens.
        out.push({ id: piece.id, poly: stripSegmentQuad(a, b, A1_CHANNEL.width), base: z, top: z + A1_CHANNEL.height, part: 'can' });
        out.push({ id: piece.id, poly: stripSegmentQuad(a, b, A1_CHANNEL.width - 4), base: z - 1, top: z + 3, part: 'lens' });
      } else out.push({ id: piece.id, poly: stripSegmentQuad(a, b), base: z - STRIP_HEIGHT / 2, top: z + STRIP_HEIGHT / 2 });
    }
  }
  return out;
}

/** A drawing from before the A1 channel: the 30 mm (`A1_SKIRTING_SET_BACK`) an A1
 *  strip set its bench's skirting back by is undone where the front board now
 *  takes the channel, carved into it and the closure flush behind it. */
export function migrateA1SetBack(inst: ProductInstance): void {
  const a1 = new Set(stripsOf(inst).flatMap((l) => (l.pos === 'A1' && 'bench' in l.host ? [l.host.bench] : [])));
  const fix = (b: { id?: string; skirtingSetBack?: number; slats?: number[]; gaps?: number[]; slatThickness?: number[]; thickness?: number }): void => {
    if (b.id && a1.has(b.id) && b.skirtingSetBack === A1_SKIRTING_SET_BACK && frontTakesChannel(b)) delete b.skirtingSetBack;
  };
  inst.polyBenches?.forEach(fix);
  for (const iw of inst.innerWalls ?? []) for (const leg of [...(iw.benches?.a ?? []), ...(iw.benches?.b ?? [])]) fix(leg);
}

// ---- Beams ------------------------------------------------------------------------

/** A beam as a section draws it: how far it reaches and how wide it opens. */
export interface BeamCone { length: number; angle: number }
/** One size per kind of light, for every section of the drawing: linear lights
 *  (strips, coves, channels) and spots (a fibre end's is a short tick, no cone). */
export const BEAM_DEFAULTS: BeamCones = {
  linear: { length: 250, angle: 30 },
  spot: { length: 600, angle: 40 },
};
/** The drawing's beam sizes (`Drawing.lightBeams`, else `BEAM_DEFAULTS`). */
export interface BeamCones { linear: BeamCone; spot: BeamCone }

/** A light's beam, the ONE rule every output reads (a section, the light levels,
 *  the 3D view): its length the drawing's for its type (`cones`), its angle its
 *  product's (`lightBeamAngle`), else the drawing's for its type — but a strip at
 *  a skirting's foot (A2) and a cove wash what is around them, so theirs opens to
 *  an LED's spread (`LED_SPREAD`). Null: a fibre end or a sky, which have none. */
export function lightBeam(light: RoomLight, cones: BeamCones = BEAM_DEFAULTS): BeamCone | null {
  if (light.kind === 'sky' || (light.kind === 'spot' && light.fibre)) return null;
  const size = light.kind === 'spot' ? cones.spot : cones.linear;
  const wash = light.kind === 'cove' || (light.kind === 'strip' && light.pos === 'A2');
  return { length: size.length, angle: lightBeamAngle(light, wash ? LED_SPREAD : size.angle) };
}

/** A point of a section view: `t` along it, `h` up. */
export interface BeamPt { t: number; h: number }

/** Where a ray from `o` along `d` first meets segment p–q (its distance), or null. */
function raySegment(o: BeamPt, d: BeamPt, p: BeamPt, q: BeamPt): number | null {
  const et = q.t - p.t, eh = q.h - p.h, den = d.t * eh - d.h * et;
  if (Math.abs(den) < 1e-12) return null;
  const s = ((p.t - o.t) * eh - (p.h - o.h) * et) / den, w = ((p.t - o.t) * d.h - (p.h - o.h) * d.t) / den;
  return s >= 0 && w >= 0 && w <= 1 ? s : null;
}

/** The part of a beam its light can see: a fan of rays across the cone, each
 *  stopped at the first solid it meets (the solids' rings, in the view). Its
 *  apex first. */
export function beamFan(apex: BeamPt, axis: number, cone: BeamCone, solids: readonly BeamPt[][], rays = 48): BeamPt[] {
  const half = ((cone.angle / 2) * Math.PI) / 180;
  const out: BeamPt[] = [{ ...apex }];
  for (let i = 0; i <= rays; i++) {
    const a = axis - half + (2 * half * i) / rays;
    const d = { t: Math.cos(a), h: Math.sin(a) };
    let reach = cone.length;
    for (const ring of solids) {
      for (let k = 0; k < ring.length; k++) {
        const hit = raySegment(apex, d, ring[k], ring[(k + 1) % ring.length]);
        if (hit != null && hit > 1 && hit < reach) reach = hit;
      }
    }
    out.push({ t: apex.t + d.t * reach, h: apex.h + d.h * reach });
  }
  return out;
}

// ---- Ceiling lights: spots and fibre points ---------------------------------------

export type SpotLight = Extract<RoomLight, { kind: 'spot' }>;
/** A spot's diameter, and a single fibre end's lens (mm). */
export const SPOT_DIAMETER = 68;
export const FIBRE_DIAMETER = 10;
/** How far a spot's can rises into the ceiling, and its trim hangs under it (mm). */
const SPOT_CAN = 34;
const SPOT_TRIM = 8;
/** Where a spot's light leaves it: the middle of its lens, under the ceiling (mm). */
export const SPOT_LENS_DROP = SPOT_TRIM - 1;
/** A spot's can, cut or seen. */
export const SPOT_BODY_COLOR = '#3a3d46';

export type Vec3 = [number, number, number];

export function spotsOf(inst: ProductInstance): SpotLight[] {
  return lightsOf(inst).filter((l): l is SpotLight => l.kind === 'spot');
}

/** The ceiling's underside over a plan point (room local): its height, and its
 *  normal pointing DOWN into the room — a flat ceiling's level, a shaped one's
 *  surface, whatever its shape. */
export function ceilingUnder(inst: ProductInstance, p: Vec2): { z: number; down: Vec3 } {
  if (!hasCeiling(inst) || isFlatCeiling(inst)) {
    // A cove's second layer, where it hangs, is the ceiling there.
    const layer = coveOf(inst) ? coveGeometry(inst)?.secondLayer : undefined;
    return { z: layer && pointInPolygon(p, layer.ring) ? layer.z0 : interiorHeight(inst), down: [0, 0, -1] };
  }
  const cf = resolveCeiling(inst);
  const n = surfaceNormal(cf, 'under', activePiece(cf, 'under', p), p);
  return { z: underAt(cf, p), down: [-n[0], -n[1], -n[2]] };
}

/** Where a spot or a fibre point is: flush in the ceiling over its point, shining
 *  square to the ceiling — or straight down, aimed plumb (a fibre always is). */
export interface SpotMount { at: Vec2; z: number; axis: Vec3; diameter: number }

export function spotMount(inst: ProductInstance, light: SpotLight): SpotMount {
  const c = ceilingUnder(inst, light.at);
  const plumb = !!light.aimPlumb || !!light.fibre;
  return { at: light.at, z: c.z, axis: plumb ? [0, 0, -1] : c.down, diameter: light.fibre ? FIBRE_DIAMETER : SPOT_DIAMETER };
}

/** The spot drawn under a point (room local), within `tol` of its ring. */
export function spotAt(inst: ProductInstance, p: Vec2, tol: number): SpotLight | null {
  let best: SpotLight | null = null, bd = Infinity;
  for (const s of spotsOf(inst)) {
    const d = dist(p, s.at), r = (s.fibre ? FIBRE_DIAMETER : SPOT_DIAMETER) / 2;
    if (d <= r + tol && d < bd) { best = s; bd = d; }
  }
  return best;
}

/** The axes a room's ceiling lights line up on (room local): `u` along its
 *  longest wall, `v` square to it. */
export function roomAxes(inst: ProductInstance): { u: Vec2; v: Vec2 } {
  const edges = polyEdges(wallView(inst));
  const u = edges.length ? edges.reduce((m, e) => (e.len > m.len ? e : m)).dir : { x: 1, y: 0 };
  return { u, v: { x: -u.y, y: u.x } };
}

/** How far off a spot's line another spot's centre may be and still stop it —
 *  be measured to, be a stop halfway from (mm). */
export const SPOT_LINE_TOL = 2;

/** A line a spot is held on that is not another spot's projection (room local,
 *  `a`–`b` the guide drawn): the room's centre line — halfway between the walls
 *  either side —, halfway between the stops either side (a spot and a wall, or
 *  two spots), or a bench's front. */
export interface SpotGuide { kind: SpotGuideKind; a: Vec2; b: Vec2 }
/** What holds a point besides another spot's projection: the room's centre line,
 *  halfway between stops, a bench front, a channel's line, the spacing of the
 *  channels before it, or an offset from a wall. */
export type SpotGuideKind =
  | 'bench'
  | 'centre'
  | 'channel'
  | 'half'
  | 'offset'
  | 'spacing';

/** More lines to hold a point on (`spotAlign`): along an axis, the coordinates
 *  `c` it may be held at, each its guide's kind (drawn across the room) or the
 *  point it lines up with (`from`); `skip` the axes (0: along the longest wall,
 *  1: square to it) left free. */
export interface AlignExtra { lines?: (ax: Vec2) => Array<{ c: number; kind?: SpotGuideKind; from?: Vec2 }>; skip?: readonly number[] }

/** The shortest a bench front is to hold a spot over it (mm). */
const BENCH_FRONT_MIN = 100;

/**
 * Where a spot put or dragged to `p` (room local) goes. Along each of the room's
 * axes it is held, within `tol`, on the nearest of: another spot's orthogonal
 * projection (`from`, the nearer spot of a tie), the front of a bench square to
 * that axis and beside it, or the point halfway between its stops either way
 * along that axis — the walls (the room's centre line), or a spot in line and a
 * wall, or two spots (`guides`).
 */
export function spotAlign(inst: ProductInstance, p: Vec2, tol: number, except?: string, extra: AlignExtra = {}): { at: Vec2; from: Vec2[]; guides: SpotGuide[] } {
  const others = spotsOf(inst).filter((s) => s.id !== except);
  const axes = Object.values(roomAxes(inst));
  const ring = polyInnerCarved(inst);
  const dot = (a: Vec2, b: Vec2): number => a.x * b.x + a.y * b.y;
  const fronts: Array<[Vec2, Vec2]> = [];
  for (const l of roomBenchLines(inst)) {
    if (l.kind !== 'band') continue;
    const pts = l.path(l.depth);
    for (let k = 1; k < pts.length; k++) if (dist(pts[k - 1], pts[k]) >= BENCH_FRONT_MIN) fronts.push([pts[k - 1], pts[k]]);
  }
  type Hold = { d: number; c: number; from?: Vec2; guide?: SpotGuide; across?: SpotGuideKind };
  const skip = new Set(extra.skip ?? []);
  const holds = axes.map((ax, k): Hold | null => {
    if (skip.has(k)) return null;
    let best: Hold | null = null;
    const offer = (c: number, h: Omit<Hold, 'd' | 'c'>): void => {
      const d = Math.abs(dot(p, ax) - c);
      if (d > tol) return;
      const nearer = best?.from && h.from ? dist(p, h.from) < dist(p, best.from) : false;
      if (!best || d < best.d - 1e-6 || (d <= best.d + 1e-6 && nearer)) best = { d, c, ...h };
    };
    for (const s of others) offer(dot(s.at, ax), { from: s.at });
    for (const [a, b] of fronts) {
      const len = dist(a, b), dir = { x: (b.x - a.x) / len, y: (b.y - a.y) / len }, t = dot({ x: p.x - a.x, y: p.y - a.y }, dir);
      if (Math.abs(dot(dir, ax)) < 0.02 && t >= -tol && t <= len + tol) offer(dot(a, ax), { guide: { kind: 'bench', a, b } });
    }
    for (const l of extra.lines?.(ax) ?? []) offer(l.c, l.from ? { from: l.from } : { across: l.kind });
    return best;
  });
  const holdOn = (q: Vec2, ax: Vec2, c: number): Vec2 => { const off = dot(q, ax) - c; return { x: q.x - ax.x * off, y: q.y - ax.y * off }; };
  let at = p;
  axes.forEach((ax, k) => { const h = holds[k]; if (h) at = holdOn(at, ax, h.c); });
  // Halfway between the stops either way — on the line the other axis holds, so
  // the spots in line with it are stops too —, when nearer than what holds it.
  axes.forEach((ax, k) => {
    if (skip.has(k) || !pointInPolygon(at, ring)) return;
    const fw = spotStops(ring, others, at, ax), bw = spotStops(ring, others, at, { x: -ax.x, y: -ax.y });
    if (!Number.isFinite(fw.wall) || !Number.isFinite(bw.wall)) return;
    const c = dot(at, ax) + (Math.min(fw.wall, fw.spot) - Math.min(bw.wall, bw.spot)) / 2, d = Math.abs(dot(p, ax) - c);
    if (d > tol || (holds[k] && d >= holds[k]!.d)) return;
    at = holdOn(at, ax, c);
    const across = { x: -ax.y, y: ax.x }, ends = [across, { x: -across.x, y: -across.y }].map((n) => {
      const t = spotStops(ring, [], at, n).wall;
      return Number.isFinite(t) ? { x: at.x + n.x * t, y: at.y + n.y * t } : at;
    });
    const walls = !Number.isFinite(fw.spot) && !Number.isFinite(bw.spot);
    holds[k] = { d, c, guide: { kind: walls ? 'centre' : 'half', a: ends[0], b: ends[1] } };
  });
  // A line held on that is drawn across the room: through the point, square to its axis.
  axes.forEach((ax, k) => {
    const h = holds[k];
    if (!h?.across) return;
    const across = { x: -ax.y, y: ax.x }, ends = [across, { x: -across.x, y: -across.y }].map((n) => {
      const t = spotStops(ring, [], at, n).wall;
      return Number.isFinite(t) ? { x: at.x + n.x * t, y: at.y + n.y * t } : at;
    });
    h.guide = { kind: h.across, a: ends[0], b: ends[1] };
  });
  const from = holds.flatMap((h) => (h?.from ? [h.from] : []));
  const guides = holds.flatMap((h) => (h?.guide ? [h.guide] : []));
  return { at, from, guides };
}

/** Along a ray from `p` (room local, unit `d`): how far to the inner ring's
 *  face, and to the nearest spot short of it whose centre the ray passes within
 *  `SPOT_LINE_TOL` of (Infinity when none). */
function spotStops(ring: Vec2[], others: SpotLight[], p: Vec2, d: Vec2): { wall: number; spot: number } {
  let wall = Infinity;
  for (let i = 0; i < ring.length; i++) {
    const t = rayCross(p, d, ring[i], ring[(i + 1) % ring.length]);
    if (t != null && t > 1e-6 && t < wall) wall = t;
  }
  let spot = Infinity;
  for (const s of others) {
    const t = (s.at.x - p.x) * d.x + (s.at.y - p.y) * d.y, off = Math.abs((s.at.x - p.x) * d.y - (s.at.y - p.y) * d.x);
    if (off <= SPOT_LINE_TOL && t > SPOT_LINE_TOL && t < wall && t < spot) spot = t;
  }
  return { wall, spot };
}

/** A spot's figure: from its centre `a` to `b` — the wall's inner face, or a
 *  spot it lines up with —, on its own line (lane 0) or a lane beside it. */
export interface SpotFigure { a: Vec2; b: Vec2; to: 'wall' | 'spot'; lane: 0 | 1 }

/** The figures of a spot at `p` (room local), along the room's axes both ways:
 *  to the nearest spot in line (`spotStops`), else to the wall's inner face;
 *  past such a spot, the wall's figure too, a lane out. */
export function spotFigures(inst: ProductInstance, p: Vec2, except?: string): SpotFigure[] {
  const ring = polyInnerCarved(inst);
  if (!pointInPolygon(p, ring)) return [];
  const others = spotsOf(inst).filter((s) => s.id !== except);
  const { u, v } = roomAxes(inst);
  const out: SpotFigure[] = [];
  for (const d of [u, { x: -u.x, y: -u.y }, v, { x: -v.x, y: -v.y }]) {
    const { wall, spot } = spotStops(ring, others, p, d);
    if (!Number.isFinite(wall)) continue;
    const at = (t: number): Vec2 => ({ x: p.x + d.x * t, y: p.y + d.y * t });
    if (Number.isFinite(spot)) out.push({ a: p, b: at(spot), to: 'spot', lane: 0 }, { a: p, b: at(wall), to: 'wall', lane: 1 });
    else out.push({ a: p, b: at(wall), to: 'wall', lane: 0 });
  }
  return out;
}

/** How far along a ray (from `p`, unit `d`) it crosses segment c–d, or null. */
function rayCross(p: Vec2, d: Vec2, c: Vec2, e: Vec2): number | null {
  const sx = e.x - c.x, sy = e.y - c.y, den = d.x * sy - d.y * sx;
  if (Math.abs(den) < 1e-12) return null;
  const t = ((c.x - p.x) * sy - (c.y - p.y) * sx) / den, w = ((c.x - p.x) * d.y - (c.y - p.y) * d.x) / den;
  return w >= 0 && w <= 1 ? t : null;
}

/** A circle as a ring of points (room local). */
export function circleRing(c: Vec2, r: number, n = 24): Vec2[] {
  return Array.from({ length: n }, (_, k) => ({ x: c.x + r * Math.cos((2 * Math.PI * k) / n), y: c.y + r * Math.sin((2 * Math.PI * k) / n) }));
}

/** Every spot and fibre point of a room as solids (room local): a spot's can
 *  rising into the ceiling and its trim under it, a fibre end's short tick. */
export function spotSolids(inst: ProductInstance): Array<{ id: string; poly: Vec2[]; base: number; top: number; part: 'can' | 'lens' }> {
  const out: Array<{ id: string; poly: Vec2[]; base: number; top: number; part: 'can' | 'lens' }> = [];
  for (const s of spotsOf(inst)) {
    const m = spotMount(inst, s), r = m.diameter / 2;
    if (s.fibre) { out.push({ id: s.id, poly: circleRing(s.at, r, 12), base: m.z - 20, top: m.z, part: 'lens' }); continue; }
    out.push({ id: s.id, poly: circleRing(s.at, r), base: m.z - SPOT_TRIM, top: m.z + SPOT_CAN, part: 'can' });
    out.push({ id: s.id, poly: circleRing(s.at, r - 8), base: m.z - SPOT_TRIM - 2, top: m.z - SPOT_TRIM + 4, part: 'lens' });
  }
  return out;
}

// ---- Channels -----------------------------------------------------------------

export type ChannelLight = Extract<RoomLight, { kind: 'channel' }>;
/** A channel's groove: its width, and how deep it runs into the ceiling (mm). */
export const CHANNEL_WIDTH = 20;
export const CHANNEL_DEPTH = 25;
/** How far a channel laid along a wall or across the room stops short of the
 *  walls at its ends (mm). */
export const CHANNEL_END_GAP = 150;
/** The longest piece a channel is drawn in under a shaped ceiling, each at its
 *  own height (mm). */
const CHANNEL_PIECE = 250;

export function channelsOf(inst: ProductInstance): ChannelLight[] {
  return lightsOf(inst).filter((l): l is ChannelLight => l.kind === 'channel');
}

export function channelFigures(ch: ChannelLight): { width: number; depth: number } {
  const ok = (v: number | undefined, d: number): number => (v != null && Number.isFinite(v) && v > 0 ? v : d);
  return { width: ok(ch.width, CHANNEL_WIDTH), depth: ok(ch.depth, CHANNEL_DEPTH) };
}

/** A path's length (mm). */
export function pathLengthOf(pts: readonly Vec2[]): number {
  return pts.reduce((s, p, i) => (i ? s + dist(pts[i - 1], p) : 0), 0);
}

/** Every channel of a room as solids (room local): along each segment — in
 *  pieces of at most `CHANNEL_PIECE` under a shaped ceiling, each at its own
 *  height — its profile rising its depth into the ceiling, and its light at the
 *  groove's mouth. */
export function channelSolids(inst: ProductInstance): Array<{ id: string; poly: Vec2[]; base: number; top: number; part: 'can' | 'lens' }> {
  const out: Array<{ id: string; poly: Vec2[]; base: number; top: number; part: 'can' | 'lens' }> = [];
  const flat = !hasCeiling(inst) || isFlatCeiling(inst);
  for (const ch of channelsOf(inst)) {
    const { width, depth } = channelFigures(ch);
    // Mitred at its bends (and where a ring closes), as the plan draws it.
    const can = pathSides(ch.pts, width), lens = pathSides(ch.pts, Math.max(2, width - 4));
    if (!can || !lens) continue;
    for (let k = 0; k + 1 < ch.pts.length; k++) {
      const a = ch.pts[k], b = ch.pts[k + 1], n = flat ? 1 : Math.max(1, Math.ceil(dist(a, b) / CHANNEL_PIECE));
      const quad = ([L, R]: [Vec2[], Vec2[]], t0: number, t1: number): Vec2[] =>
        [lerpPt(L[k], L[k + 1], t0), lerpPt(L[k], L[k + 1], t1), lerpPt(R[k], R[k + 1], t1), lerpPt(R[k], R[k + 1], t0)];
      for (let i = 0; i < n; i++) {
        const z = ceilingUnder(inst, lerpPt(a, b, (i + 0.5) / n)).z;
        out.push({ id: ch.id, poly: quad(can, i / n, (i + 1) / n), base: z, top: z + depth, part: 'can' });
        out.push({ id: ch.id, poly: quad(lens, i / n, (i + 1) / n), base: z - 1, top: z + 3, part: 'lens' });
      }
    }
  }
  return out;
}

/** The channel drawn under a point (room local), within `tol` of its groove. */
export function channelAt(inst: ProductInstance, p: Vec2, tol: number): ChannelLight | null {
  let best: ChannelLight | null = null, bd = Infinity;
  for (const ch of channelsOf(inst)) {
    const d = pathDistance(p, ch.pts);
    if (d <= channelFigures(ch).width / 2 + tol && d < bd) { best = ch; bd = d; }
  }
  return best;
}

/** A wall's inner face (WALL numbered, room local): from where the wall before
 *  it meets it to where the wall after it does, and its normal into the room. */
export function wallInnerFace(inst: ProductInstance, w: number): { a: Vec2; b: Vec2; dir: Vec2; n: Vec2 } | null {
  const edges = polyEdges(wallView(inst)), N = edges.length;
  const e = edges[w];
  if (!e || N < 3) return null;
  const T = wallThicknessOf(inst);
  const line = (k: number): [Vec2, Vec2] => {
    const f = edges[(k + N) % N], o = { x: f.a.x + f.normal.x * T, y: f.a.y + f.normal.y * T };
    return [o, { x: o.x + f.dir.x, y: o.y + f.dir.y }];
  };
  const [p, q] = line(w), [p0, q0] = line(w - 1), [p1, q1] = line(w + 1);
  const a = crossing(p0, q0, p, q) ?? p, b = crossing(p, q, p1, q1) ?? { x: p.x + e.dir.x * e.len, y: p.y + e.dir.y * e.len };
  return { a, b, dir: e.dir, n: e.normal };
}

/** A channel along wall `w`, `offset` mm in from its inner face, stopping `gap`
 *  short of the walls at its ends — or null where it would not fit. */
export function channelAlongWall(inst: ProductInstance, w: number, offset: number, gap = CHANNEL_END_GAP): Vec2[] | null {
  return channelAnchorPath(inst, { walls: [w], offset, startGap: gap, endGap: gap });
}

/**
 * The path of a channel laid along walls (room local): each wall's inner face
 * moved `offset` in, mitred where two of its walls meet. A ring runs round every
 * wall, its first point repeated last; an open one runs from where its first
 * wall's line meets the face of the wall before it to where its last meets the
 * face of the wall after, less its end gaps. Null where it would not fit.
 */
export function channelAnchorPath(inst: ProductInstance, a: ChannelAnchor): Vec2[] | null {
  const L = wallLines(inst), N = L.length, o = Math.max(0, a.offset);
  if (N < 3) return null;
  if (a.closed) {
    const ring: Vec2[] = [];
    for (let w = 0; w < N; w++) {
      const p = linesMeet(L, (w - 1 + N) % N, o, w, o);
      if (!p) return null;
      ring.push(p);
    }
    const pts = dedupe(ring);
    return pts.length >= 3 ? [...pts, { ...pts[0] }] : null;
  }
  const walls = a.walls.filter((w) => w >= 0 && w < N);
  if (!walls.length) return null;
  const first = walls[0], last = walls[walls.length - 1];
  const start = linesMeet(L, first, o, (first - 1 + N) % N, 0), end = linesMeet(L, last, o, (last + 1) % N, 0);
  if (!start || !end) return null;
  const pts: Vec2[] = [start];
  for (let k = 0; k + 1 < walls.length; k++) {
    const p = linesMeet(L, walls[k], o, walls[k + 1], o);
    if (p) pts.push(p);
  }
  pts.push(end);
  const out = trimPath(dedupe(pts), Math.max(0, a.startGap ?? CHANNEL_END_GAP), Math.max(0, a.endGap ?? CHANNEL_END_GAP));
  return out.length >= 2 ? out : null;
}

/** A channel laid along walls carried on along wall `w` — the wall after its last
 *  or the one before its first, not one of its own —, else null. */
export function chainedAnchor(inst: ProductInstance, a: ChannelAnchor, w: number): ChannelAnchor | null {
  const N = polyEdges(wallView(inst)).length;
  if (a.closed || !a.walls.length || a.walls.includes(w) || N < 3) return null;
  if (w === (a.walls[a.walls.length - 1] + 1) % N) return { ...a, walls: [...a.walls, w] };
  if (w === (a.walls[0] - 1 + N) % N) return { ...a, walls: [w, ...a.walls] };
  return null;
}

/** A channel laid along walls laid down again (to the mm) — what keeps it on them
 *  after the room is edited; an anchor that no longer fits is let go, the channel
 *  staying where it was. Whether it changed. */
export function syncChannel(inst: ProductInstance, ch: ChannelLight): boolean {
  if (!ch.anchor) return false;
  const pts = channelAnchorPath(inst, ch.anchor);
  if (!pts) { delete ch.anchor; return true; }
  const r = pts.map((p) => ({ x: Math.round(p.x), y: Math.round(p.y) }));
  if (r.length === ch.pts.length && r.every((p, i) => p.x === ch.pts[i].x && p.y === ch.pts[i].y)) return false;
  ch.pts = r;
  return true;
}

/** Every channel of a room laid along walls laid down again; whether any changed. */
export function syncChannels(inst: ProductInstance): boolean {
  let changed = false;
  for (const ch of channelsOf(inst)) changed = syncChannel(inst, ch) || changed;
  return changed;
}

/** The wall (WALL numbered) segment `k` of a channel laid along walls runs along. */
export function channelWallOf(ch: ChannelLight, k: number): number | null {
  const a = ch.anchor;
  if (!a) return null;
  if (a.closed) return k;
  return a.walls.length ? a.walls[Math.max(0, Math.min(a.walls.length - 1, k))] : null;
}

/** Segment `k` of a channel moved by `delta` (room local): one laid along walls
 *  goes as far further in as `delta` carries it square to that wall — its offset,
 *  every wall's —, a drawn one moves that segment's ends (a ring's shared point
 *  both ways). */
export function shiftChannelSegment(inst: ProductInstance, ch: ChannelLight, k: number, delta: Vec2): void {
  const w = channelWallOf(ch, k), f = w != null ? wallInnerFace(inst, w) : null;
  if (ch.anchor && f) {
    ch.anchor.offset = Math.max(0, Math.round(ch.anchor.offset + delta.x * f.n.x + delta.y * f.n.y));
    syncChannel(inst, ch);
    return;
  }
  const last = ch.pts.length - 1, ring = isRingPath(ch.pts);
  const move = new Set([k, k + 1]);
  if (ring && (move.has(0) || move.has(last))) { move.add(0); move.add(last); }
  ch.pts = ch.pts.map((p, i) => (move.has(i) ? { x: Math.round(p.x + delta.x), y: Math.round(p.y + delta.y) } : p));
}

/** A whole channel moved by `delta` (room local); one laid along walls only as
 *  far as it carries it square to its first wall. */
export function moveChannel(inst: ProductInstance, ch: ChannelLight, delta: Vec2): void {
  if (ch.anchor) { shiftChannelSegment(inst, ch, 0, delta); return; }
  ch.pts = ch.pts.map((p) => ({ x: Math.round(p.x + delta.x), y: Math.round(p.y + delta.y) }));
}

/** How far a channel's free end (`end` 0: its start) stops short of the wall on
 *  along its line (Infinity when its line meets none). */
export function channelEndGap(inst: ProductInstance, pts: readonly Vec2[], end: 0 | 1): number {
  const ring = polyInnerCarved(inst), n = pts.length - 1;
  if (n < 1) return Infinity;
  const p = end ? pts[n] : pts[0], q = end ? pts[n - 1] : pts[1], L = dist(p, q);
  if (L < 1e-9) return Infinity;
  const d = { x: (p.x - q.x) / L, y: (p.y - q.y) / L };
  let t = Infinity;
  for (let i = 0; i < ring.length; i++) {
    const x = rayCross(p, d, ring[i], ring[(i + 1) % ring.length]);
    if (x != null && x > 1e-6 && x < t) t = x;
  }
  return t;
}

/** A channel's free end set `gap` short of the wall on along its line: one laid
 *  along walls takes it as its gap; a drawn one's end moves along its line. */
export function setChannelGap(inst: ProductInstance, ch: ChannelLight, end: 0 | 1, gap: number): void {
  const g = Math.max(0, Math.round(gap));
  if (ch.anchor && !ch.anchor.closed) {
    if (end) ch.anchor.endGap = g; else ch.anchor.startGap = g;
    syncChannel(inst, ch);
    return;
  }
  const t = channelEndGap(inst, ch.pts, end), n = ch.pts.length - 1;
  if (!Number.isFinite(t)) return;
  const p = end ? ch.pts[n] : ch.pts[0], q = end ? ch.pts[n - 1] : ch.pts[1], L = dist(p, q), k = t - g;
  const moved = { x: Math.round(p.x + ((p.x - q.x) / L) * k), y: Math.round(p.y + ((p.y - q.y) / L) * k) };
  ch.pts = ch.pts.map((x, i) => (i === (end ? n : 0) ? moved : x));
}

/** Where parallel channels at `at` (across them, any order) go to be spaced
 *  evenly: the outer two stay and the rest share the room between them; or
 *  `spacing` apart from the first. In the order given. */
export function distributedPositions(at: readonly number[], spacing?: number): number[] {
  const order = at.map((c, i) => ({ c, i })).sort((a, b) => a.c - b.c);
  const out = [...at], n = order.length;
  if (n < 2) return out;
  const c0 = order[0].c, step = spacing ?? (order[n - 1].c - c0) / (n - 1);
  order.forEach(({ i }, k) => { out[i] = c0 + k * step; });
  return out;
}

/** Whether a path is a ring (its first point repeated last). */
export function isRingPath(pts: readonly Vec2[]): boolean {
  return pts.length > 3 && dist(pts[0], pts[pts.length - 1]) < 0.01;
}

/** The wall whose inner face is nearest a point (room local), and how far in the
 *  point is from it. */
export function nearestWall(inst: ProductInstance, p: Vec2): { wall: number; offset: number } | null {
  let best: { wall: number; offset: number } | null = null, bd = Infinity;
  for (let w = 0; w < polyEdges(wallView(inst)).length; w++) {
    const f = wallInnerFace(inst, w);
    if (!f) continue;
    const d = pathDistance(p, [f.a, f.b]);
    if (d < bd) { bd = d; best = { wall: w, offset: (p.x - f.a.x) * f.n.x + (p.y - f.a.y) * f.n.y }; }
  }
  return best;
}

/** How the Channel tool's runs are laid: `across` the room (else along its
 *  longest wall), the outer two `margin` from the walls, or `spacing` apart (as
 *  many as fit, centred, the outer ones at least `margin` — half the spacing — in),
 *  stopping `gap` short of the walls at their ends. */
export interface ChannelRunsOptions { across?: boolean; margin?: number; spacing?: number; gap?: number }

/** Where the runs lie across the room (`v0`–`v1` wide): `n` in the middles of
 *  equal shares; or the outer two `margin` in, the rest even between; or every
 *  `spacing`, centred. */
export function runPositions(v0: number, v1: number, n: number, o: Pick<ChannelRunsOptions, 'margin' | 'spacing'> = {}): number[] {
  const W = v1 - v0;
  if (o.spacing && o.spacing > 0) {
    const m = o.margin ?? o.spacing / 2, count = Math.max(1, Math.floor((W - 2 * m) / o.spacing + 1e-6) + 1);
    const start = (v0 + v1) / 2 - ((count - 1) * o.spacing) / 2;
    return Array.from({ length: count }, (_, k) => start + k * o.spacing!);
  }
  if (n < 1) return [];
  if (o.margin != null && n > 1) return Array.from({ length: n }, (_, k) => v0 + o.margin! + (k * (W - 2 * o.margin!)) / (n - 1));
  return Array.from({ length: n }, (_, k) => v0 + ((k + 0.5) * W) / n);
}

/** Channels evenly across a room (`runPositions`), each along its longest wall's
 *  direction (`roomAxes`) — or square to it, `across` —, stopping `gap`
 *  (`CHANNEL_END_GAP`) short of the walls. A line crossing the room in several
 *  pieces (an L, a U) gives one channel per piece. */
export function channelRuns(inst: ProductInstance, n: number, opts: ChannelRunsOptions = {}): Vec2[][] {
  const ring = polyInnerCarved(inst);
  if (ring.length < 3 || !polyEdges(wallView(inst)).length) return [];
  const axes = roomAxes(inst), u = opts.across ? axes.v : axes.u, v = opts.across ? axes.u : axes.v;
  const gap = Math.max(0, opts.gap ?? CHANNEL_END_GAP);
  const along = ring.map((p) => p.x * u.x + p.y * u.y), across = ring.map((p) => p.x * v.x + p.y * v.y);
  const u0 = Math.min(...along) - 1, u1 = Math.max(...along) + 1, v0 = Math.min(...across), v1 = Math.max(...across);
  const out: Vec2[][] = [];
  for (const c of runPositions(v0, v1, n, opts)) {
    const a = { x: u.x * u0 + v.x * c, y: u.y * u0 + v.y * c }, b = { x: u.x * u1 + v.x * c, y: u.y * u1 + v.y * c };
    const ts: number[] = [];
    for (let i = 0; i < ring.length; i++) {
      const x = segmentCross(a, b, ring[i], ring[(i + 1) % ring.length]);
      if (x != null) ts.push(x);
    }
    ts.sort((p, q) => p - q);
    for (let i = 0; i + 1 < ts.length; i += 2) {
      const piece = trimPath([lerpPt(a, b, ts[i]), lerpPt(a, b, ts[i + 1])], gap, gap);
      if (piece.length >= 2) out.push(piece);
    }
  }
  return out;
}

const lerpPt = (a: Vec2, b: Vec2, t: number): Vec2 => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });

/** Where segment a–b crosses segment c–d, as a fraction of a–b, or null. */
function segmentCross(a: Vec2, b: Vec2, c: Vec2, d: Vec2): number | null {
  const rx = b.x - a.x, ry = b.y - a.y, sx = d.x - c.x, sy = d.y - c.y;
  const den = rx * sy - ry * sx;
  if (Math.abs(den) < 1e-12) return null;
  const t = ((c.x - a.x) * sy - (c.y - a.y) * sx) / den, w = ((c.x - a.x) * ry - (c.y - a.y) * rx) / den;
  return t >= 0 && t <= 1 && w >= 0 && w < 1 ? t : null;
}

/** A channel's figure: from `a` to `b` (room local) — square to its segment
 *  `seg` out to a wall or a channel parallel to it (`offset`), or on from a free
 *  end along its line to the wall (`gap`, `end` 0 its start, 1 its end) —, on its
 *  own line (lane 0) or a lane beside it. */
export interface ChannelDim { a: Vec2; b: Vec2; to: 'channel' | 'wall'; lane: 0 | 1; kind: 'gap' | 'offset'; seg: number; end?: 0 | 1 }

/** Two directions within 2° of parallel (either way). */
const parallel = (d: Vec2, e: Vec2): boolean => Math.abs(d.x * e.y - d.y * e.x) < 0.035;

/**
 * The figures of a channel along `pts` (room local) in its room: for each
 * segment, from its middle square out to the nearer wall's inner face (past a
 * channel parallel to it on the way, that one's figure too, the wall's a lane
 * out) and, the other way, to a channel parallel to it (once a pair); and each
 * free end's gap on to the wall. `except` is the channel itself, stored.
 */
export function channelDims(inst: ProductInstance, pts: Vec2[], except?: string): ChannelDim[] {
  const ring = polyInnerCarved(inst);
  if (pts.length < 2 || ring.length < 3) return [];
  const closed = isRingPath(pts), n = pts.length - 1;
  const segs: Array<{ a: Vec2; b: Vec2; own: number }> = [];
  for (const ch of channelsOf(inst)) if (ch.id !== except) for (let k = 0; k + 1 < ch.pts.length; k++) segs.push({ a: ch.pts[k], b: ch.pts[k + 1], own: -1 });
  for (let k = 0; k < n; k++) segs.push({ a: pts[k], b: pts[k + 1], own: k });
  const wallRay = (p: Vec2, d: Vec2): number => {
    let t = Infinity;
    for (let i = 0; i < ring.length; i++) {
      const x = rayCross(p, d, ring[i], ring[(i + 1) % ring.length]);
      if (x != null && x > 1e-6 && x < t) t = x;
    }
    return t;
  };
  const adjacent = (i: number, k: number): boolean => Math.abs(i - k) === 1 || (closed && Math.abs(i - k) === n - 1);
  const chanRay = (p: Vec2, d: Vec2, k: number, dir: Vec2): { t: number; idx: number } => {
    let t = Infinity, idx = -1;
    segs.forEach((s, i) => {
      if (s.own === k || (s.own >= 0 && adjacent(s.own, k))) return;
      const L = dist(s.a, s.b);
      if (L < 1 || !parallel(dir, { x: (s.b.x - s.a.x) / L, y: (s.b.y - s.a.y) / L })) return;
      const x = rayCross(p, d, s.a, s.b);
      if (x != null && x > 1 && x < t) { t = x; idx = i; }
    });
    return { t, idx };
  };
  const out: ChannelDim[] = [], seen = new Set<string>();
  for (let k = 0; k < n; k++) {
    const a = pts[k], b = pts[k + 1], len = dist(a, b);
    if (len < 1) continue;
    const dir = { x: (b.x - a.x) / len, y: (b.y - a.y) / len }, m = lerpPt(a, b, 0.5);
    if (!pointInPolygon(m, ring)) continue;
    const sides = [{ x: -dir.y, y: dir.x }, { x: dir.y, y: -dir.x }].map((d) => ({ d, wall: wallRay(m, d), ch: chanRay(m, d, k, dir) }));
    const near = sides[0].wall <= sides[1].wall ? 0 : 1;
    sides.forEach(({ d, wall, ch }, side) => {
      const at = (t: number): Vec2 => ({ x: m.x + d.x * t, y: m.y + d.y * t });
      if (Number.isFinite(ch.t) && ch.t < wall) {
        const other = segs[ch.idx], key = other.own >= 0 ? [k, other.own].sort().join('-') : `${k}:${ch.idx}`;
        if (!seen.has(key)) { seen.add(key); out.push({ a: m, b: at(ch.t), to: 'channel', lane: 0, kind: 'offset', seg: k }); }
        if (side === near && Number.isFinite(wall)) out.push({ a: m, b: at(wall), to: 'wall', lane: 1, kind: 'offset', seg: k });
      } else if (side === near && Number.isFinite(wall)) out.push({ a: m, b: at(wall), to: 'wall', lane: 0, kind: 'offset', seg: k });
    });
  }
  if (!closed) {
    for (const end of [0, 1] as const) {
      const p = end ? pts[n] : pts[0], q = end ? pts[n - 1] : pts[1], L = dist(p, q);
      if (L < 1) continue;
      const d = { x: (p.x - q.x) / L, y: (p.y - q.y) / L }, t = wallRay(p, d);
      if (Number.isFinite(t) && pointInPolygon(p, ring)) out.push({ a: p, b: { x: p.x + d.x * t, y: p.y + d.y * t }, to: 'wall', lane: 0, kind: 'gap', seg: end ? n - 1 : 0, end });
    }
  }
  return out;
}

/** The offsets from a wall's inner face a point of a channel is held at (mm). */
export const CHANNEL_WALL_OFFSETS = [100, 150, 200, 300];

/**
 * Where a point of a channel drawn or moved to `p` (room local) goes: held as a
 * spot is (`spotAlign`: the spots' projections, the room's centre lines, halfway
 * between stops, the bench fronts) and besides, along each axis, on: another
 * channel's line square to it and every channel's ends and corners (`from`), the
 * next line at the same spacing as two channels before it, and
 * `CHANNEL_WALL_OFFSETS` in from a wall square to it. `skip`: the axes left free
 * (one a segment drawn square to the room keeps); `except`: the channel itself.
 */
export function channelAlign(inst: ProductInstance, p: Vec2, tol: number, opts: { except?: string; skip?: readonly number[] } = {}): { at: Vec2; from: Vec2[]; guides: SpotGuide[] } {
  const chans = channelsOf(inst).filter((c) => c.id !== opts.except);
  const faces = polyEdges(wallView(inst)).map((_, w) => wallInnerFace(inst, w)).filter((f): f is NonNullable<typeof f> => !!f);
  const dot = (a: Vec2, b: Vec2): number => a.x * b.x + a.y * b.y;
  const lines = (ax: Vec2): Array<{ c: number; kind?: SpotGuideKind; from?: Vec2 }> => {
    const out: Array<{ c: number; kind?: SpotGuideKind; from?: Vec2 }> = [];
    const cs: number[] = [];
    for (const ch of chans) {
      ch.pts.forEach((q) => out.push({ c: dot(q, ax), from: q }));
      for (let k = 0; k + 1 < ch.pts.length; k++) {
        const a = ch.pts[k], b = ch.pts[k + 1], L = dist(a, b);
        if (L >= 1 && Math.abs(dot({ x: (b.x - a.x) / L, y: (b.y - a.y) / L }, ax)) < 0.02) { out.push({ c: dot(a, ax), kind: 'channel' }); cs.push(dot(a, ax)); }
      }
    }
    const sorted = [...new Set(cs.map((c) => Math.round(c)))].sort((x, y) => x - y);
    for (let i = 0; i + 1 < sorted.length; i++) {
      const g = sorted[i + 1] - sorted[i];
      if (g > 1) out.push({ c: sorted[i + 1] + g, kind: 'spacing' }, { c: sorted[i] - g, kind: 'spacing' });
    }
    for (const f of faces) {
      if (Math.abs(dot(f.dir, ax)) > 0.02) continue;
      const side = Math.sign(dot(f.n, ax));
      for (const o of CHANNEL_WALL_OFFSETS) out.push({ c: dot(f.a, ax) + side * o, kind: 'offset' });
    }
    return out;
  };
  return spotAlign(inst, p, tol, undefined, { lines, skip: opts.skip });
}

/** What a channel runs into that it should not: a wall too near for its profile's
 *  flange (`wall`), the heater's keep-clear zone (`heater`), the cove's second
 *  layer's edge or its strip (`cove`), a spot (`spot`), or a ridge or other sudden
 *  change of slope in a shaped ceiling (`ridge`). */
export type ChannelWarning =
  | 'cove'
  | 'heater'
  | 'ridge'
  | 'spot'
  | 'wall';
/** How far each side of its groove a channel's profile needs (mm). */
export const CHANNEL_FLANGE = 15;
/** How far a light keeps from a heater's plan footprint (mm). */
export const HEATER_KEEP_CLEAR = 300;

/** The shortest distance between two segments. */
function segDistance(a: Vec2, b: Vec2, c: Vec2, d: Vec2): number {
  if (segmentCross(a, b, c, d) != null) return 0;
  return Math.min(pathDistance(a, [c, d]), pathDistance(b, [c, d]), pathDistance(c, [a, b]), pathDistance(d, [a, b]));
}

/** How near a path comes to a polygon's outline (0 where it crosses it or lies inside). */
function pathToPolygon(pts: Vec2[], poly: Vec2[]): number {
  if (pts.some((p) => pointInPolygon(p, poly))) return 0;
  let best = Infinity;
  for (let k = 0; k + 1 < pts.length; k++) for (let i = 0; i < poly.length; i++) best = Math.min(best, segDistance(pts[k], pts[k + 1], poly[i], poly[(i + 1) % poly.length]));
  return best;
}

/** What a channel runs into (`ChannelWarning`), in that order; `heaters` are the
 *  plan footprints (room local) of the heaters standing in its room — its heater
 *  walls count too. */
export function channelWarnings(inst: ProductInstance, ch: ChannelLight, heaters: Vec2[][] = []): ChannelWarning[] {
  const pts = ch.pts, { width } = channelFigures(ch), out: ChannelWarning[] = [];
  if (pts.length < 2) return out;
  const ring = polyInnerCarved(inst);
  let wall = Infinity;
  if (pts.some((p) => !pointInPolygon(p, ring))) wall = 0;
  else for (let k = 0; k + 1 < pts.length; k++) for (let i = 0; i < ring.length; i++) wall = Math.min(wall, segDistance(pts[k], pts[k + 1], ring[i], ring[(i + 1) % ring.length]));
  if (wall < width / 2 + CHANNEL_FLANGE - 1e-6) out.push('wall');
  const hot = [...heaters, ...heaterWallBands(inst).map((h) => h.poly)];
  if (hot.some((h) => pathToPolygon(pts, h) < HEATER_KEEP_CLEAR + width / 2)) out.push('heater');
  const cg = coveOf(inst) ? coveGeometry(inst) : null;
  const layer = cg?.secondLayer?.ring;
  const crossesLayer = !!layer && pts.some((p) => pointInPolygon(p, layer)) && pts.some((p) => !pointInPolygon(p, layer));
  if (crossesLayer || (cg?.runs ?? []).some((r) => {
    for (let k = 0; k + 1 < pts.length; k++) for (let i = 0; i + 1 < r.pts.length; i++) if (segDistance(pts[k], pts[k + 1], r.pts[i], r.pts[i + 1]) < width / 2 + CHANNEL_FLANGE) return true;
    return false;
  })) out.push('cove');
  if (spotsOf(inst).some((sp) => pathDistance(sp.at, pts) < width / 2 + (sp.fibre ? FIBRE_DIAMETER : SPOT_DIAMETER) / 2)) out.push('spot');
  if (hasCeiling(inst) && !isFlatCeiling(inst)) {
    let prev: Vec3 | null = null;
    outer: for (let k = 0; k + 1 < pts.length; k++) {
      const n = Math.max(1, Math.ceil(dist(pts[k], pts[k + 1]) / 50));
      for (let i = 0; i <= n; i++) {
        const d = ceilingUnder(inst, lerpPt(pts[k], pts[k + 1], i / n)).down;
        if (prev && prev[0] * d[0] + prev[1] * d[1] + prev[2] * d[2] < Math.cos((3 * Math.PI) / 180)) { out.push('ridge'); break outer; }
        prev = d;
      }
    }
  }
  return out;
}

// ---- Coves ------------------------------------------------------------------------

export type CoveLight = Extract<RoomLight, { kind: 'cove' }>;
/** A flat ceiling's cove: its second layer stops `gap` short of the cove walls,
 *  its bottom `ledgeThickness` runs `ledge` further toward them, a `valance`
 *  square stands on the ledge's edge (mm). */
export const COVE_DEFAULTS = { gap: 150, ledge: 50, ledgeThickness: 20, valance: 20 };
/** A Coved ceiling's cove: the ledge under the springing line the strip stands on,
 *  and how far off the wall the strip is (mm). */
export const COVE_SPRING_LEDGE = { depth: 70, thickness: 24, strip: 22 };
/** How a cove's strip shines: up, into the room washing a Coved ceiling's curve,
 *  or toward the wall from a flat ceiling's ledge (degrees). */
const COVE_TILT_SPRINGING = 18;
const COVE_TILT_LAYER = -28;

export function coveOf(inst: ProductInstance): CoveLight | null {
  return lightsOf(inst).find((l): l is CoveLight => l.kind === 'cove') ?? null;
}

export function coveFigures(cove: CoveLight): typeof COVE_DEFAULTS {
  const l = cove.secondLayer ?? {};
  const ok = (v: number | undefined, d: number): number => (v != null && Number.isFinite(v) && v >= 0 ? v : d);
  const gap = ok(l.gap, COVE_DEFAULTS.gap), ledge = Math.min(ok(l.ledge, COVE_DEFAULTS.ledge), gap);
  return { gap, ledge, ledgeThickness: ok(l.ledgeThickness, COVE_DEFAULTS.ledgeThickness), valance: Math.min(ok(l.valance, COVE_DEFAULTS.valance), ledge) };
}

/** How a room's ceiling takes a cove: on a Coved ceiling's springing line, with a
 *  second layer under a flat one, or not at all (any other shape). */
export function coveKind(inst: ProductInstance): 'springing' | 'layer' | null {
  if (!hasCeiling(inst)) return null;
  if (isFlatCeiling(inst)) return 'layer';
  return ceilingShapeOf(inst) === 'cove' ? 'springing' : null;
}

/** Each wall's inner face as a line (WALL numbered, room local): a point on it,
 *  its direction and its normal into the room. */
function wallLines(inst: ProductInstance): Array<{ o: Vec2; dir: Vec2; n: Vec2 }> {
  const T = wallThicknessOf(inst);
  return polyEdges(wallView(inst)).map((e) => ({ o: { x: e.a.x + e.normal.x * T, y: e.a.y + e.normal.y * T }, dir: e.dir, n: e.normal }));
}

/** Where wall k's line moved in by `ok` meets wall j's moved in by `oj`. */
function linesMeet(L: ReturnType<typeof wallLines>, k: number, ok: number, j: number, oj: number): Vec2 | null {
  const a = L[k], b = L[j];
  const pa = { x: a.o.x + a.n.x * ok, y: a.o.y + a.n.y * ok }, pb = { x: b.o.x + b.n.x * oj, y: b.o.y + b.n.y * oj };
  return crossing(pa, { x: pa.x + a.dir.x, y: pa.y + a.dir.y }, pb, { x: pb.x + b.dir.x, y: pb.y + b.dir.y });
}

/**
 * The lines a room's walls give a cove, `o` mm in from their inner faces, round
 * its corners: `corner(v, op, oi)` is the path at corner `v` from the wall before
 * it (`op` in) to the wall after it (`oi` in). At a sharp corner the point the two
 * lines meet; at a rounded one the arc concentric with the wall's (as a bench's
 * ring, sharp once its radius runs out) — where one line is further in than the
 * other, it runs to where it meets the other's arc, or straight face, and on
 * round it.
 */
function coveCorners(inst: ProductInstance): { L: ReturnType<typeof wallLines>; corner: (v: number, op: number, oi: number) => Vec2[] } {
  const L = wallLines(inst), N = L.length;
  const view = wallView(inst), edges = polyEdges(view), corners = roomCorners(view), T = wallThicknessOf(inst);
  const at = (e: PolyEdge, s: number, o: number): Vec2 => ({ x: e.a.x + e.dir.x * s + e.normal.x * (T + o), y: e.a.y + e.dir.y * s + e.normal.y * (T + o) });
  const cross = (a: Vec2, u: Vec2, b: Vec2, c: Vec2): { t: number; x: Vec2 } | null => {
    // Where the line through a along u crosses the segment b→c (t along it).
    const v = { x: c.x - b.x, y: c.y - b.y }, den = u.x * v.y - u.y * v.x;
    if (Math.abs(den) < 1e-12) return null;
    const t = (u.x * (a.y - b.y) - u.y * (a.x - b.x)) / den;
    return t >= -1e-9 && t <= 1 + 1e-9 ? { t, x: { x: b.x + v.x * t, y: b.y + v.y * t } } : null;
  };
  const corner = (v: number, op: number, oi: number): Vec2[] => {
    const p = (v - 1 + N) % N, i = v, c = corners[v], ep = edges[p], ei = edges[i];
    const meet = (): Vec2[] => { const x = linesMeet(L, p, op, i, oi); return x ? [x] : []; };
    if (!c || !ep || !ei) return meet();
    const m = Math.min(op, oi), r = faceRadius(c, T + m);
    if (!(r > RADIUS_EPS)) return meet();
    // In two halves, so its middle — where two cove walls' ledges meet — is on it.
    const a0 = at(ep, ep.len - c.station, m), a1 = at(ei, c.station, m);
    const u0 = unit({ x: a0.x - c.center.x, y: a0.y - c.center.y }), u1 = unit({ x: a1.x - c.center.x, y: a1.y - c.center.y });
    const um = unit({ x: u0.x + u1.x, y: u0.y + u1.y }), mid = { x: c.center.x + um.x * r, y: c.center.y + um.y * r };
    const arc = [...arcPoints(c.center, a0, mid, r), ...arcPoints(c.center, mid, a1, r).slice(1)];
    if (Math.abs(op - oi) < 1e-6) return arc;
    // A line crossing the arc's chord crosses the arc itself near there.
    const onCircle = (a: Vec2, u: Vec2, near: Vec2): Vec2 => {
      const w = { x: a.x - c.center.x, y: a.y - c.center.y }, b = w.x * u.x + w.y * u.y, q = b * b - (w.x * w.x + w.y * w.y - r * r);
      if (q < 0) return near;
      const t = [-b - Math.sqrt(q), -b + Math.sqrt(q)].map((k) => ({ x: a.x + u.x * k, y: a.y + u.y * k }));
      return dist(t[0], near) < dist(t[1], near) ? t[0] : t[1];
    };
    if (op > oi) {
      // The wall before's line, further in, meets the arc (or the wall after's face) and runs on round it.
      const path = [...arc, at(ei, ei.len, oi)], u = L[p].dir, a = at(ep, 0, op);
      for (let k = 0; k + 1 < path.length; k++) {
        const hit = cross(a, u, path[k], path[k + 1]);
        if (hit) return [k + 1 < arc.length ? onCircle(a, u, hit.x) : hit.x, ...path.slice(k + 1, path.length - 1)];
      }
      return meet();
    }
    const path = [at(ep, 0, op), ...arc], u = L[i].dir, a = at(ei, 0, oi);
    for (let k = path.length - 2; k >= 0; k--) {
      const hit = cross(a, u, path[k], path[k + 1]);
      if (hit) return [...path.slice(1, k + 1), k > 0 ? onCircle(a, u, hit.x) : hit.x];
    }
    return meet();
  };
  return { L, corner };
}

/** A path split at the middle of its length: the first half (ending there) and
 *  the second (starting there). */
function splitHalf(pts: Vec2[]): [Vec2[], Vec2[]] {
  if (pts.length < 2) return [pts, pts];
  const half = pathLength(pts) / 2;
  let s = 0;
  for (let k = 1; k < pts.length; k++) {
    const l = dist(pts[k - 1], pts[k]);
    if (s + l >= half) {
      const m = lerpPt(pts[k - 1], pts[k], l > 0 ? (half - s) / l : 0);
      return [[...pts.slice(0, k), m], [m, ...pts.slice(k)]];
    }
    s += l;
  }
  return [pts, [pts[pts.length - 1]]];
}

/** What a room's cove is made of (room local): a flat ceiling's second layer, the
 *  ledges and valances, and the strip runs — or null where the room's ceiling
 *  takes none or it names no wall. */
export interface CoveGeometry {
  kind: 'springing' | 'layer';
  /** The second layer: its ring, underside and top. */
  secondLayer?: { ring: Vec2[]; z0: number; z1: number };
  parts: Array<{ poly: Vec2[]; base: number; top: number; part: 'layer' | 'ledge' | 'valance' }>;
  runs: Array<{ pts: Vec2[]; z: number; shine: Shine; side: 1 | -1; closed: boolean; length: number; segments: CoveSegment[] }>;
}
/** A cove run as LEDs, in path order (the electrical schematic's boxes): its
 *  stretch along one wall (`wall`), or round one rounded corner (`corner`, the
 *  vertex); a sharp corner none. */
export interface CoveSegment { wall?: number; corner?: number; pts: Vec2[]; length: number }

/**
 * A room's cove, round its corners as its walls go (`coveCorners`): the second
 * layer's ring `gap` in from the cove walls and on the others' faces; each cove
 * wall's ledge and valance, ending half way round a corner it shares with another
 * cove wall and on the face of one it does not; the strip along them, one run
 * per chain of cove walls — a ring when every wall is one.
 */
export function coveGeometry(inst: ProductInstance): CoveGeometry | null {
  // Room-local: where the room stands and how it is turned change none of it.
  return COVES.get(inst.id, () => ({ ...inst, x: undefined, y: undefined, rotation: undefined }), () => coveGeometryOf(inst));
}
/** Every room's cove, kept while the room (placement aside) holds: the plan, the
 *  sections, the RCP, the 3D view, the hit tests and the tables each ask, many
 *  times a frame (385 µs a time for a rounded room). */
const COVES = contentMemo<CoveGeometry | null>({ skip: HEAVY_FIELDS });

function coveGeometryOf(inst: ProductInstance): CoveGeometry | null {
  const cove = coveOf(inst), kind = coveKind(inst);
  const { L, corner } = coveCorners(inst), N = L.length;
  if (!cove || !kind || N < 3) return null;
  const on = new Set(cove.walls.filter((w) => w >= 0 && w < N));
  if (!on.size) return null;
  const f = coveFigures(cove);
  const prev = (w: number): number => (w - 1 + N) % N, next = (w: number): number => (w + 1) % N;
  const dedup = (pts: Vec2[]): Vec2[] => pts.filter((p, k) => k === 0 || dist(p, pts[k - 1]) > 0.01);
  const fromFace = (w: number, p: Vec2): number => (p.x - L[w].o.x) * L[w].n.x + (p.y - L[w].o.y) * L[w].n.y;
  /** Wall w's band between offsets o0 < o1: along the wall, half way round a
   *  corner it shares with another cove wall, else closed along the other's face. */
  const band = (w: number, o0: number, o1: number): Vec2[] | null => {
    const v0 = w, v1 = next(w);
    const head = (o: number): Vec2[] => (on.has(prev(w)) ? splitHalf(corner(v0, o, o))[1] : corner(v0, 0, o).slice(-1));
    const tail = (o: number): Vec2[] => (on.has(next(w)) ? splitHalf(corner(v1, o, o))[0] : corner(v1, o, 0).slice(0, 1));
    const between = (pts: Vec2[]): Vec2[] => pts.filter((p) => { const d = fromFace(w, p); return d > o0 + 0.01 && d < o1 - 0.01; });
    const outer = [...head(o1), ...tail(o1)], inner = [...head(o0), ...tail(o0)];
    if (outer.length < 2 || inner.length < 2) return null;
    const closeEnd = on.has(next(w)) ? [] : between(corner(v1, 0, 0)).reverse();
    const closeStart = on.has(prev(w)) ? [] : between(corner(v0, 0, 0));
    return dedup([...outer, ...closeEnd, ...inner.reverse(), ...closeStart]);
  };
  const parts: CoveGeometry['parts'] = [];
  let secondLayer: CoveGeometry['secondLayer'];
  let d: number, z: number, shine: Shine;
  if (kind === 'layer') {
    const H = interiorHeight(inst), T = ceilingThicknessOf(inst), z0 = H - T;
    const ring = dedup(Array.from({ length: N }, (_, k) => corner(k, on.has(prev(k)) ? f.gap : 0, on.has(k) ? f.gap : 0)).flat());
    if (ring.length > 2 && dist(ring[0], ring[ring.length - 1]) < 0.01) ring.pop();
    secondLayer = { ring, z0, z1: H };
    parts.push({ poly: ring, base: z0, top: H, part: 'layer' });
    for (const w of on) {
      const ledge = band(w, f.gap - f.ledge, f.gap), valance = band(w, f.gap - f.ledge, f.gap - f.ledge + f.valance);
      if (ledge) parts.push({ poly: ledge, base: z0, top: z0 + f.ledgeThickness, part: 'ledge' });
      if (valance && f.valance > 0) parts.push({ poly: valance, base: z0 + f.ledgeThickness, top: z0 + f.ledgeThickness + f.valance, part: 'valance' });
    }
    d = f.gap - (f.ledge - f.valance) / 2;
    z = z0 + f.ledgeThickness + STRIP_HEIGHT / 2;
    shine = { up: true, tilt: COVE_TILT_LAYER };
  } else {
    const cf = resolveCeiling(inst), S = cf.springing ?? cf.interiorEave;
    for (const w of on) {
      const ledge = band(w, 0, COVE_SPRING_LEDGE.depth);
      if (ledge) parts.push({ poly: ledge, base: S - COVE_SPRING_LEDGE.thickness, top: S, part: 'ledge' });
    }
    d = COVE_SPRING_LEDGE.strip;
    z = S + STRIP_HEIGHT / 2;
    shine = { up: true, tilt: COVE_TILT_SPRINGING };
  }
  // The strip: along each cove wall `d` in, round the corners between cove
  // walls, stopping `STRIP_INSET` short of the walls it ends at.
  const runs: CoveGeometry['runs'] = [];
  const walls = [...on].sort((a, b) => a - b);
  const all = walls.length === N;
  // Start each chain at a cove wall whose wall before it is not one (any, for a ring).
  const starts = all ? [walls[0]] : walls.filter((w) => !on.has(prev(w)));
  for (const s0 of starts) {
    let pts: Vec2[];
    if (all) {
      pts = dedup(Array.from({ length: N }, (_, k) => corner((s0 + k) % N, d, d)).flat());
      if (pts.length) pts.push({ ...pts[0] });
    } else {
      pts = corner(s0, 0, d).slice(-1);
      for (let w = s0; on.has(w); w = next(w)) {
        pts.push(...(on.has(next(w)) ? corner(next(w), d, d) : corner(next(w), d, 0).slice(0, 1)));
        if (!on.has(next(w))) break;
      }
      pts = dedup(pts);
    }
    const path = all ? pts : trimPath(pts, STRIP_INSET, STRIP_INSET);
    if (path.length < 2) continue;
    // The room is on the walls' normals' side: left of a path along the walls'
    // directions when they turn that way.
    const side = sideOf({ x: 0, y: 0 }, L[s0].dir, L[s0].n);
    // As LEDs: wall w from its corner to the next's, a rounded corner its own.
    const segments: CoveSegment[] = [];
    const add = (p: Vec2[], at: { wall: number } | { corner: number }): void => {
      const q = dedup(p);
      if (q.length >= 2 && pathLength(q) > 0.01) segments.push({ ...at, pts: q, length: pathLength(q) });
    };
    if (all) {
      for (let k = 0; k < N; k++) {
        const v = (s0 + k) % N, c = dedup(corner(v, d, d)), nx = dedup(corner(next(v), d, d));
        add(c, { corner: v });
        add([c[c.length - 1], nx[0]], { wall: v });
      }
    } else {
      let cur = corner(s0, 0, d).slice(-1)[0];
      for (let w = s0; on.has(w); w = next(w)) {
        const last = !on.has(next(w));
        const c = last ? corner(next(w), d, 0).slice(0, 1) : dedup(corner(next(w), d, d));
        add([cur, c[0]], { wall: w });
        if (last) break;
        add(c, { corner: next(w) });
        cur = c[c.length - 1];
      }
      const set = (k: number, a: number, b: number): void => {
        const q = trimPath(segments[k].pts, a, b);
        if (q.length >= 2) segments[k] = { ...segments[k], pts: q, length: pathLength(q) };
        else segments.splice(k, 1);
      };
      if (segments.length) set(segments.length - 1, segments.length === 1 ? STRIP_INSET : 0, STRIP_INSET);
      if (segments.length > 1) set(0, STRIP_INSET, 0);
    }
    runs.push({ pts: path, z, shine, side, closed: all, length: pathLength(path), segments });
  }
  return { kind, ...(secondLayer ? { secondLayer } : {}), parts, runs };
}

/** An LED strip's own spread (°): how wide a section draws the beam of a light
 *  that washes what it faces — a Coved ceiling's cove, a strip at a skirting's
 *  foot (A2, 12 mm over the floor, where the drawing's cone would end on the
 *  floor within a few centimetres) — rather than a narrow cone. */
export const LED_SPREAD = 120;

/** What a cove's light fills in a section, square to its wall: a flat ceiling's
 *  recess — from the wall to the second layer's edge, from the opening up to the
 *  ceiling, less the ledge and the valance — and the wash spilling out of the
 *  opening down the wall, as the outline (`d` mm in from the wall's inner face,
 *  `z` up) starting at the strip. Null for a Coved ceiling's cove: its light
 *  opens to `LED_SPREAD` until the curve or the wall stops it. */
export function coveRecess(inst: ProductInstance): Array<{ d: number; z: number }> | null {
  const cove = coveOf(inst), g = cove ? coveGeometry(inst) : null;
  if (!cove || !g?.secondLayer || !g.runs.length) return null;
  const f = coveFigures(cove), { z0, z1 } = g.secondLayer;
  const lip = f.gap - f.ledge, top = z0 + f.ledgeThickness, spill = 1.5 * f.gap;
  const strip = { d: f.gap - (f.ledge - f.valance) / 2, z: g.runs[0].z };
  return [
    strip, { d: f.gap, z: top }, { d: f.gap, z: z1 }, { d: 0, z: z1 }, { d: 0, z: z0 - spill },
    { d: lip, z: z0 }, { d: lip, z: top + f.valance }, { d: lip + f.valance, z: top + f.valance }, { d: lip + f.valance, z: top },
  ];
}

/** A cove's strips as solids (room local): a strip's box along each segment of
 *  each run, mitred where two meet. */
export function coveStripSolids(inst: ProductInstance): Array<{ id: string; poly: Vec2[]; base: number; top: number }> {
  const cove = coveOf(inst), g = cove ? coveGeometry(inst) : null;
  if (!cove || !g) return [];
  const out: Array<{ id: string; poly: Vec2[]; base: number; top: number }> = [];
  for (const run of g.runs) {
    // Mitred at its corners and round its arcs, closed where a ring is.
    const sides = pathSides(run.pts, STRIP_WIDTH);
    for (let k = 0; k + 1 < run.pts.length; k++) {
      const poly = sides ? [sides[0][k], sides[0][k + 1], sides[1][k + 1], sides[1][k]] : stripSegmentQuad(run.pts[k], run.pts[k + 1]);
      out.push({ id: cove.id, poly, base: run.z - STRIP_HEIGHT / 2, top: run.z + STRIP_HEIGHT / 2 });
    }
  }
  return out;
}

/** The cove wall nearest a point (room local) within `reach` of its inner face, or null. */
export function coveWallAt(inst: ProductInstance, p: Vec2, reach: number): number | null {
  const w = nearestWall(inst, p);
  return w && w.offset >= -1 && w.offset <= reach ? w.wall : null;
}

// ---- Starry sky -------------------------------------------------------------------

export type SkyLight = Extract<RoomLight, { kind: 'sky' }>;
/** A starry sky's figures: how far its zone stays off the walls, and how far a
 *  fibre stays clear of another light (mm). */
export const SKY_DEFAULTS = { margin: 150, clear: 50 };
/** A sky's fibre end: a bare fibre (mm). */
export const SKY_FIBRE_DIAMETER = 1;
/** One arc is drawn for every this many fibres, at least one. */
export const FIBRES_PER_ARC = 10;
/** A fibre projector's box (mm), when its product gives none. */
export const PROJECTOR_SIZE = { width: 300, depth: 200, height: 110 };
/** How far outside its room a projector placed outside it stands (mm). */
export const PROJECTOR_OUTSIDE = 300;
/** How many draws each fibre is the best of: the one furthest from those already
 *  placed wins (Mitchell's best candidate), so the sky reads even, not gridded. */
const SKY_CANDIDATES = 24;

export function skiesOf(inst: ProductInstance): SkyLight[] {
  return lightsOf(inst).filter((l): l is SkyLight => l.kind === 'sky');
}

export function skyFigures(sky: SkyLight): typeof SKY_DEFAULTS {
  const ok = (v: number | undefined, d: number): number => (v != null && Number.isFinite(v) && v >= 0 ? v : d);
  return { margin: ok(sky.margin, SKY_DEFAULTS.margin), clear: ok(sky.clear, SKY_DEFAULTS.clear) };
}

/** How many fibres a sky has: its kit's. */
export function skyCount(sky: SkyLight): number {
  return Math.max(0, Math.min(500, Math.round(sky.fibres) || 0));
}

/** Where a sky's fibres may be (room local): inside its drawn zone and the
 *  walls' inner faces, else inside those faces and at least its margin off them.
 *  `bounds` is the ring the points are drawn in, `key` what the scatter depends on. */
function skyRegion(inst: ProductInstance, sky: SkyLight): { bounds: Vec2[]; key: unknown[]; inside: (p: Vec2) => boolean } | null {
  const inner = resolvedOutline(inst, wallThicknessOf(inst));
  if (inner.length < 3) return null;
  if (sky.zone && sky.zone.length >= 3) {
    const zone = sky.zone;
    return { bounds: zone, key: [zone, inner], inside: (p) => pointInPolygon(p, zone) && pointInPolygon(p, inner) };
  }
  const { margin } = skyFigures(sky), closed = [...inner, inner[0]];
  return { bounds: inner, key: [inner, margin], inside: (p) => pointInPolygon(p, inner) && pathDistance(p, closed) >= margin };
}

/** The ring a sky's zone is drawn as (room local): its drawn zone, else the
 *  walls' inner faces less its margin; empty when the margin leaves nothing. */
export function skyZone(inst: ProductInstance, sky: SkyLight): Vec2[] {
  if (sky.zone && sky.zone.length >= 3) return sky.zone;
  const inner = resolvedOutline(inst, wallThicknessOf(inst)), { margin } = skyFigures(sky);
  const ring = resolvedOutline(inst, wallThicknessOf(inst) + margin), closed = [...inner, inner[0]];
  // An inset past the room's narrowest turns inside out yet keeps its turn: a
  // corner nearer the walls than the margin gives it away.
  return ring.length >= 3 && ring.every((p) => pointInPolygon(p, inner) && pathDistance(p, closed) >= margin - 0.5) ? ring : [];
}

/** The shortest zone edge that takes a margin handle (mm): a rounded corner's
 *  chords take none. */
const MARGIN_HANDLE_EDGE = 150;

/** A whole-ceiling sky's margin handles (room local): the middle of each straight
 *  edge of its zone and the normal there pointing into the zone, away from the
 *  wall — a handle moved along it by `t` makes the margin `margin + t`. None for a
 *  drawn zone, or a margin that leaves no zone. */
export function skyMarginHandles(inst: ProductInstance, sky: SkyLight): Array<{ at: Vec2; n: Vec2 }> {
  if (sky.zone && sky.zone.length >= 3) return [];
  const ring = skyZone(inst, sky);
  if (ring.length < 3) return [];
  const turn = Math.sign(ring.reduce((s, p, i) => { const q = ring[(i + 1) % ring.length]; return s + p.x * q.y - q.x * p.y; }, 0)) || 1;
  const out: Array<{ at: Vec2; n: Vec2 }> = [];
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i], b = ring[(i + 1) % ring.length], len = dist(a, b);
    if (len < MARGIN_HANDLE_EDGE) continue;
    out.push({ at: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, n: { x: (-(b.y - a.y) / len) * turn, y: ((b.x - a.x) / len) * turn } });
  }
  return out;
}

/** A small seeded random generator (mulberry32): the same seed, the same sky. */
export function seededRandom(seed: number): () => number {
  let a = seed | 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const SCATTERS = new Map<string, Vec2[]>();

/** `n` points scattered evenly but randomly over a region, from `seed`. A point
 *  depends only on the ones before it, so the first 50 of a 100-fibre sky are
 *  the 50-fibre sky's. */
function scatter(region: NonNullable<ReturnType<typeof skyRegion>>, n: number, seed: number): Vec2[] {
  const key = JSON.stringify([region.key, n, seed]);
  const hit = SCATTERS.get(key);
  if (hit) return hit;
  const xs = region.bounds.map((p) => p.x), ys = region.bounds.map((p) => p.y);
  const x0 = Math.min(...xs), y0 = Math.min(...ys), w = Math.max(...xs) - x0, h = Math.max(...ys) - y0;
  const { inside } = region;
  const rnd = seededRandom(seed), pts: Vec2[] = [], limit = n * SKY_CANDIDATES * 50;
  let draws = 0;
  while (pts.length < n && draws < limit) {
    let best: Vec2 | null = null, bd = -1;
    for (let k = 0; k < SKY_CANDIDATES && draws < limit;) {
      draws++;
      const c = { x: x0 + rnd() * w, y: y0 + rnd() * h };
      if (!inside(c)) continue;
      k++;
      let d = Infinity;
      for (const q of pts) d = Math.min(d, (q.x - c.x) ** 2 + (q.y - c.y) ** 2);
      if (d > bd) { bd = d; best = c; }
    }
    if (!best) break;
    pts.push(best);
  }
  if (SCATTERS.size >= 64) SCATTERS.delete(SCATTERS.keys().next().value as string);
  SCATTERS.set(key, pts);
  return pts;
}

/** Whether a point is too near what a sky's fibres keep clear of (room local):
 *  the room's other ceiling lights — spots and fibre points, channels, a cove's
 *  ledges and valances — and the partitions and heater walls that reach the
 *  ceiling, by the sky's `clear`. */
function skyBlocked(inst: ProductInstance, sky: SkyLight): (p: Vec2) => boolean {
  const { clear } = skyFigures(sky), H = interiorHeight(inst);
  const tests: Array<(p: Vec2) => boolean> = [];
  for (const s of spotsOf(inst)) {
    const r = (s.fibre ? FIBRE_DIAMETER : SPOT_DIAMETER) / 2 + clear;
    tests.push((p) => dist(p, s.at) < r);
  }
  for (const ch of channelsOf(inst)) {
    const r = channelFigures(ch).width / 2 + clear;
    tests.push((p) => pathDistance(p, ch.pts) < r);
  }
  const rings: Vec2[][] = [];
  if (coveOf(inst)) for (const part of coveGeometry(inst)?.parts ?? []) if (part.part !== 'layer') rings.push(part.poly);
  for (const iw of inst.innerWalls ?? []) if (iw.edge != null && (iw.height == null || innerWallBase(iw) + iw.height >= H - 1)) rings.push(polyInnerWallBand(inst, iw));
  for (const { wall, poly } of heaterWallBands(inst)) if (heaterWallTop(wall) >= H - 1) rings.push(poly);
  for (const ring of rings) if (ring.length >= 3) tests.push((p) => pointInPolygon(p, ring) || pathDistance(p, [...ring, ring[0]]) < clear);
  return (p) => tests.some((t) => t(p));
}

/** The free place nearest `p`, searched on rings round it 25 mm apart out to a
 *  metre; `p` itself when there is none. */
function nudge(p: Vec2, free: (q: Vec2) => boolean): Vec2 {
  for (let r = 25; r <= 1000; r += 25) {
    const steps = Math.max(8, Math.round((2 * Math.PI * r) / 25));
    for (let k = 0; k < steps; k++) {
      const a = (2 * Math.PI * k) / steps, q = { x: p.x + r * Math.cos(a), y: p.y + r * Math.sin(a) };
      if (free(q)) return q;
    }
  }
  return p;
}

/** One fibre end of a sky: its number (what `moved` keys), where it is (room
 *  local) and the ceiling's underside there. */
export interface SkyFibre { i: number; at: Vec2; z: number }

/** A sky's fibre ends: its seeded scatter over its zone, each one that falls too
 *  near another light moved to the nearest free place — so a spot added moves
 *  only the fibres it touches —, the ones dragged by hand where they were left;
 *  each at the ceiling's underside over it, hanging plumb. */
export function skyFibres(inst: ProductInstance, sky: SkyLight): SkyFibre[] {
  const region = skyRegion(inst, sky);
  if (!region) return [];
  const base = scatter(region, skyCount(sky), Math.round(sky.seed) || 0);
  const blocked = skyBlocked(inst, sky);
  const free = (q: Vec2): boolean => region.inside(q) && !blocked(q);
  return base.map((p, i) => {
    const m = sky.moved?.[i];
    const at = m ? { x: m.x, y: m.y } : blocked(p) ? nudge(p, free) : p;
    return { i, at, z: ceilingUnder(inst, at).z };
  });
}

/** The fibre of a room's skies under a point (room local), within `tol`. */
export function skyFibreAt(inst: ProductInstance, p: Vec2, tol: number): { sky: SkyLight; fibre: SkyFibre } | null {
  let best: { sky: SkyLight; fibre: SkyFibre } | null = null, bd = Infinity;
  for (const sky of skiesOf(inst)) {
    for (const f of skyFibres(inst, sky)) {
      const d = dist(p, f.at);
      if (d <= SKY_FIBRE_DIAMETER / 2 + tol && d < bd) { best = { sky, fibre: f }; bd = d; }
    }
  }
  return best;
}

/** The fibres a projector's arcs are drawn to (their indices in `fibres`): one
 *  for every `FIBRES_PER_ARC`, at least one — taking the fibres in turn round
 *  the projector, the middle one of each share. */
export function fibreArcTargets(from: Vec2, fibres: readonly Vec2[]): number[] {
  const n = fibres.length;
  if (!n) return [];
  const arcs = Math.max(1, Math.round(n / FIBRES_PER_ARC));
  const order = fibres.map((p, i) => ({ i, a: Math.atan2(p.y - from.y, p.x - from.x) })).sort((a, b) => a.a - b.a || a.i - b.i).map((x) => x.i);
  return Array.from({ length: arcs }, (_, k) => order[Math.min(n - 1, Math.floor(((k + 0.5) * n) / arcs))]);
}

/** How far a fibre's arc stands to one side of its chord, as a share of the
 *  chord's plan length: its widest point is a twelfth of it. */
const ARC_SIDE = 1 / 12 / (Math.SQRT2 - 1);

/** A fibre's arc from its projector (room local, mm above the floor): a quarter
 *  of an ellipse in one plane, leaving the projector level and dropping onto the
 *  fibre, so every view of it — the plan, an elevation — is an ellipse's arc and
 *  it never rises over the higher of its ends. Its plane leans so its middle
 *  stands a twelfth of its plan length to one side, and arcs fanning out of one
 *  projector read apart. A kit's arcs take its plan path (x, y) and lay it over
 *  the ceiling (`drapedArc`). */
export function fibreArc(from: Vec3, to: Vec3, n = 16): Vec3[] {
  const dx = to[0] - from[0], dy = to[1] - from[1];
  // The ellipse's centre is level with the fibre's end, beside the projector:
  // P(θ) = C + (from − C) cos θ + (to − C) sin θ, θ from 0 to a right angle.
  const C: Vec3 = [from[0] + dy * ARC_SIDE, from[1] - dx * ARC_SIDE, to[2]];
  return Array.from({ length: n + 1 }, (_, k) => {
    const t = (k / n) * (Math.PI / 2), c = k === n ? 0 : Math.cos(t), s = k === n ? 1 : Math.sin(t);
    return [0, 1, 2].map((j) => C[j] + (from[j] - C[j]) * c + (to[j] - C[j]) * s) as Vec3;
  });
}

/** How far a projector (its middle, room local) is from the furthest of its
 *  fibres' ends (mm): the straight distance, nothing more. */
export function furthestFibre(from: Vec3, fibres: ReadonlyArray<{ at: Vec2; z: number }>): number {
  return fibres.reduce((m, f) => Math.max(m, Math.hypot(f.at.x - from[0], f.at.y - from[1], f.z - from[2])), 0);
}

/** A ring's middle: its centroid, or a point surely inside it when the centroid
 *  falls in a notch. */
const middleOf = (ring: Vec2[]): Vec2 => { const c = polygonCentroid(ring); return pointInPolygon(c, ring) ? c : insidePoint(ring); };

/** Where a fibre kit's projector goes by default (room local): above the
 *  ceiling over the middle of a sky's zone, or 400 mm from a fibre point toward
 *  the middle of the room (over the point when it is nearer the middle). */
export function projectorHome(inst: ProductInstance, light: SkyLight | SpotLight): Vec2 {
  const inner = resolvedOutline(inst, wallThicknessOf(inst));
  if (light.kind === 'sky') {
    const ring = skyZone(inst, light);
    return middleOf(ring.length >= 3 ? ring : inner);
  }
  const mid = middleOf(inner), d = dist(light.at, mid);
  if (d <= 400) return { ...light.at };
  return { x: light.at.x + ((mid.x - light.at.x) / d) * 400, y: light.at.y + ((mid.y - light.at.y) / d) * 400 };
}

/** Where a projector placed outside its room goes (room local): beside the wall
 *  nearest `near`, `PROJECTOR_OUTSIDE` beyond its outer face, across from `near`. */
export function projectorOutside(inst: ProductInstance, near: Vec2): Vec2 | null {
  const w = nearestWall(inst, near), face = w ? wallInnerFace(inst, w.wall) : null;
  if (!w || !face) return null;
  const s = Math.max(0, Math.min(dist(face.a, face.b), (near.x - face.a.x) * face.dir.x + (near.y - face.a.y) * face.dir.y));
  const out = wallThicknessOf(inst) + PROJECTOR_OUTSIDE + PROJECTOR_SIZE.depth / 2;
  return { x: face.a.x + face.dir.x * s - face.n.x * out, y: face.a.y + face.dir.y * s - face.n.y * out };
}

/** The height a projector placed above the ceiling stands at, over a point
 *  (mm above the room's floor): the ceiling's top there. */
export function projectorBase(inst: ProductInstance, p: Vec2): number {
  return ceilingTop(inst)(p);
}

/** The ceiling's top surface of a room (mm above its floor) over a point, room
 *  local: a slope's, a dome's, the flat slab's; beyond the walls, its edge's
 *  nearest the point. Resolved once, for sampling along a path. */
export function ceilingTop(inst: ProductInstance): (p: Vec2) => number {
  if (!hasCeiling(inst) || isFlatCeiling(inst)) {
    const z = interiorHeight(inst) + ceilingThicknessOf(inst);
    return () => z;
  }
  const cf = resolveCeiling(inst), ring = resolvedOutline(inst);
  const onEdge = (p: Vec2): Vec2 => ring.reduce((best, a, i) => {
    const q = projectToSegment(p, a, ring[(i + 1) % ring.length]);
    return dist(p, q) < dist(p, best) ? q : best;
  }, ring[0]);
  return (p) => outerAt(cf, pointInPolygon(p, ring) ? p : onEdge(p));
}

/** How high over the ceiling's top a fibre's arc runs at most (mm): it lies on
 *  the ceiling, outside it, within this. */
export const FIBRE_RUN = 150;
/** The least an arc stands off the ceiling's top before it goes through over its
 *  fibre (mm): clear of the cable's own thickness and of a dome's curve between
 *  two of its points. */
export const FIBRE_RUN_CLEAR = 10;
/** Points along a kit's arc, enough to follow a dome. */
const FIBRE_ARC_POINTS = 32;

/** A fibre's arc laid over the ceiling (room local, mm above the floor): along
 *  `plan` (the projector's middle to the fibre), each point over the ceiling's
 *  top under it (`top`) by a lift that falls from the projector's — FIBRE_RUN at
 *  most — to nothing where it goes through, as a quarter ellipse falls. So it
 *  follows a slope or a dome outside it, rising with it over the projector where
 *  the dome does. It leaves the projector's middle itself: one lifted higher
 *  drops to the band at once. */
export function drapedArc(plan: readonly Vec2[], from: Vec3, top: (p: Vec2) => number): Vec3[] {
  const n = plan.length - 1;
  const lift = Math.min(FIBRE_RUN, Math.max(FIBRE_RUN_CLEAR, from[2] - top(plan[0])));
  return plan.map((p, k): Vec3 => {
    if (k === 0) return [from[0], from[1], from[2]];
    if (k === n) return [p.x, p.y, top(p)];
    return [p.x, p.y, top(p) + Math.max(FIBRE_RUN_CLEAR, lift * Math.cos(((k / n) * Math.PI) / 2))];
  });
}

/** A sky's fibre end as a section and 3D see it: a short tick hanging plumb (mm). */
export const SKY_TICK = { radius: 2, length: 15 };

/** Every sky's fibre ends as solids (room local): a tick each, under the ceiling. */
export function skySolids(inst: ProductInstance): Array<{ id: string; poly: Vec2[]; base: number; top: number }> {
  return skiesOf(inst).flatMap((sky) => skyFibres(inst, sky).map((f) => ({ id: sky.id, poly: circleRing(f.at, SKY_TICK.radius, 6), base: f.z - SKY_TICK.length, top: f.z })));
}

/** The fibre kits of a room, each fed by a projector part: a sky, a 1-fibre point. */
export function fibreKitsOf(inst: ProductInstance): FibreKit[] {
  return lightsOf(inst).filter(isFibreKit);
}

/** A kit's fibre ends (room local, at the ceiling's underside over each). */
export function kitFibres(inst: ProductInstance, light: SkyLight | SpotLight): Array<{ at: Vec2; z: number }> {
  return light.kind === 'sky' ? skyFibres(inst, light) : [{ at: light.at, z: ceilingUnder(inst, light.at).z }];
}

/** A projector part's middle in its kit's room (room local, mm above the room's floor). */
export function projectorMiddle(room: ProductInstance, part: ProductInstance): Vec3 {
  const local = toInstanceLocal(room, { x: part.x, y: part.y }), [b, t] = partSpan(part);
  return [local.x, local.y, (b + t) / 2 - roomBase(room)];
}

/** A part's footprint in a room's frame (room local): its box, turned by its own rotation. */
export function partFootprintIn(room: ProductInstance, part: ProductInstance): Vec2[] {
  const t = (part.rotation * Math.PI) / 180, c = Math.cos(t), s = Math.sin(t), hw = part.width / 2, hd = part.depth / 2;
  return [[-hw, -hd], [hw, -hd], [hw, hd], [-hw, hd]].map(([x, y]) => toInstanceLocal(room, { x: part.x + x * c - y * s, y: part.y + x * s + y * c }));
}

/** A kit's arcs (room local, mm above the floor): from its projector's middle to
 *  the fibres `fibreArcTargets` picks, each over the ceiling's top within
 *  FIBRE_RUN of it (`drapedArc`), landing on it over its fibre, where the fibre
 *  goes through. */
export function kitArcs(inst: ProductInstance, from: Vec3, fibres: ReadonlyArray<{ at: Vec2; z: number }>): Vec3[][] {
  const top = ceilingTop(inst);
  return fibreArcTargets({ x: from[0], y: from[1] }, fibres.map((f) => f.at)).map((i) => {
    const f = fibres[i];
    const plan = fibreArc(from, [f.at.x, f.at.y, from[2]], FIBRE_ARC_POINTS).map(([x, y]) => ({ x, y }));
    return drapedArc(plan, from, top);
  });
}

// ---- The lights switched on (the 3D view's Lights on, a GLB's lights) -----------------

/** A light as it shines (room local, heights above the floor), what a section's
 *  beams, the light levels and the 3D view all read: a spot from its lens along
 *  its aim; a straight stretch of a strip, a cove's strip or a channel — its ends
 *  and the way it shines; a strip's or a cove's `lean`, the unit plan vector into
 *  the room it tilts toward —; a fibre end. `beam` is its `lightBeam`. */
export type Emitter =
  | { kind: 'spot'; id: string; at: Vec3; aim: Vec3; beam: BeamCone }
  | { kind: 'line'; id: string; role: 'strip' | 'cove'; a: Vec3; b: Vec3; aim: Vec3; lean: Vec2; beam: BeamCone }
  | { kind: 'line'; id: string; role: 'channel'; a: Vec3; b: Vec3; aim: Vec3; beam: BeamCone }
  | { kind: 'fibre'; id: string; at: Vec3 };

/** The way a strip shines (a unit vector): up or down, tilted `shine.tilt`° toward
 *  the room — `lean`, a unit plan vector. */
function shineAim(shine: Shine, lean: Vec2): Vec3 {
  const t = (shine.tilt * Math.PI) / 180;
  return [lean.x * Math.sin(t), lean.y * Math.sin(t), shine.up ? Math.cos(t) : -Math.cos(t)];
}

/** A room's lights as they shine, under the drawing's beam sizes (`cones`): each
 *  spot; each straight stretch of a strip run, of the cove's strip and of a
 *  channel (at the groove's mouth, shining square to the ceiling); each fibre
 *  end, a sky's or a fibre point's. Resolved once per room (`resolvedLighting`). */
export function lightEmitters(room: ProductInstance, cones: BeamCones = BEAM_DEFAULTS): Emitter[] {
  if (!lightsOf(room).length) return [];
  const r = resolvedLighting(room), k = JSON.stringify(cones);
  let out = r.emitters.get(k);
  if (!out) { out = emittersOf(room, cones); r.emitters.set(k, out); }
  return out;
}

function emittersOf(room: ProductInstance, cones: BeamCones): Emitter[] {
  const out: Emitter[] = [];
  const byId = new Map(lightsOf(room).map((l) => [l.id, l]));
  const lines = (light: RoomLight, role: 'strip' | 'cove', pts: Vec2[], z: number, shine: Shine, side: 1 | -1): void => {
    const beam = lightBeam(light, cones)!;
    for (let k = 0; k + 1 < pts.length; k++) {
      const a = pts[k], b = pts[k + 1], d = dist(a, b);
      if (d < 1e-6) continue;
      const lean = { x: (-(b.y - a.y) / d) * side, y: ((b.x - a.x) / d) * side };
      out.push({ kind: 'line', id: light.id, role, a: [a.x, a.y, z], b: [b.x, b.y, z], aim: shineAim(shine, lean), lean, beam });
    }
  };
  for (const run of stripRuns(room)) for (const piece of run.pieces) {
    const light = byId.get(piece.id);
    if (light) lines(light, 'strip', piece.pts, run.z, run.shine, run.side);
  }
  const cove = coveOf(room);
  if (cove) for (const run of coveGeometry(room)?.runs ?? []) lines(cove, 'cove', run.pts, run.z, run.shine, run.side);
  for (const ch of channelsOf(room)) {
    const beam = lightBeam(ch, cones)!;
    for (let k = 0; k + 1 < ch.pts.length; k++) {
      const a = ch.pts[k], b = ch.pts[k + 1];
      if (dist(a, b) < 1e-6) continue;
      const ca = ceilingUnder(room, a), cb = ceilingUnder(room, b), c = ceilingUnder(room, lerpPt(a, b, 0.5));
      out.push({ kind: 'line', id: ch.id, role: 'channel', a: [a.x, a.y, ca.z], b: [b.x, b.y, cb.z], aim: c.down, beam });
    }
  }
  for (const sp of spotsOf(room)) {
    const m = spotMount(room, sp);
    out.push(sp.fibre
      ? { kind: 'fibre', id: sp.id, at: [sp.at.x, sp.at.y, m.z - 20] }
      : { kind: 'spot', id: sp.id, at: [sp.at.x, sp.at.y, m.z - SPOT_LENS_DROP], aim: m.axis, beam: lightBeam(sp, cones)! });
  }
  for (const sky of skiesOf(room)) for (const f of skyFibres(room, sky)) out.push({ kind: 'fibre', id: sky.id, at: [f.at.x, f.at.y, f.z - SKY_TICK.length] });
  return out;
}

/** How far (mm) a 3D area light's straight piece may stray from the line light it
 *  stands for: a rounded corner's short stretches become one or two pieces. */
export const RIG_TOLERANCE = 150;

/** A line light as the 3D view lights it (three/lights3d.ts): one straight piece
 *  of a light's stretches chained end to end. `power` is the length it stands for
 *  over its own (≥ 1): its intensity scaled by that gives out what they did. */
export interface RigLine { id: string; role: 'strip' | 'cove' | 'channel'; a: Vec3; b: Vec3; aim: Vec3; power: number }

/** A room's line emitters as the 3D view's area lights: each light's stretches
 *  chained end to end, a chain's points kept only where leaving one out would
 *  stray more than `tol` (mm) from them. A straight stretch stays one light; a
 *  rounded corner's ten short ones become one or two — every area light costs
 *  every pixel of every frame, and ten to a corner made a cove round a rounded
 *  room 44 to 70 lights. */
export function rigLines(emitters: readonly Emitter[], tol = RIG_TOLERANCE): RigLine[] {
  const chains: Array<Array<Extract<Emitter, { kind: 'line' }>>> = [];
  for (const e of emitters) {
    if (e.kind !== 'line') continue;
    const chain = chains[chains.length - 1], last = chain?.[chain.length - 1];
    if (last && last.id === e.id && last.role === e.role && dist3(last.b, e.a) < 0.5) chain.push(e);
    else chains.push([e]);
  }
  const out: RigLine[] = [];
  for (const chain of chains) {
    const pts = [chain[0].a, ...chain.map((e) => e.b)];
    const keep = keptPoints(pts, tol);
    for (let k = 0; k + 1 < keep.length; k++) {
      const a = pts[keep[k]], b = pts[keep[k + 1]], chord = dist3(a, b);
      if (chord < 1e-6) continue;
      let length = 0;
      const aim: Vec3 = [0, 0, 0];
      for (const e of chain.slice(keep[k], keep[k + 1])) {
        const l = dist3(e.a, e.b);
        length += l;
        for (let i = 0; i < 3; i++) aim[i] += e.aim[i] * l;
      }
      const n = Math.hypot(...aim) || 1;
      out.push({ id: chain[0].id, role: chain[0].role, a, b, aim: [aim[0] / n, aim[1] / n, aim[2] / n], power: length / chord });
    }
  }
  return out;
}

const dist3 = (a: Vec3, b: Vec3): number => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);

/** The points of a path a piece of it keeps (their indices, in order): each one
 *  further than `tol` from the straight piece that would leave it out
 *  (Douglas–Peucker). A closed path is first cut at its point furthest from its
 *  start, as it has no chord of its own. */
function keptPoints(pts: readonly Vec3[], tol: number): number[] {
  const n = pts.length - 1;
  const off = (p: Vec3, a: Vec3, b: Vec3): number => {
    const ab: Vec3 = [b[0] - a[0], b[1] - a[1], b[2] - a[2]], len2 = ab[0] ** 2 + ab[1] ** 2 + ab[2] ** 2;
    const t = len2 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * ab[0] + (p[1] - a[1]) * ab[1] + (p[2] - a[2]) * ab[2]) / len2)) : 0;
    return dist3(p, [a[0] + ab[0] * t, a[1] + ab[1] * t, a[2] + ab[2] * t]);
  };
  const keep = new Set([0, n]);
  const split = (i: number, j: number): void => {
    let far = -1, worst = tol;
    for (let k = i + 1; k < j; k++) { const d = off(pts[k], pts[i], pts[j]); if (d > worst) { worst = d; far = k; } }
    if (far < 0) return;
    keep.add(far);
    split(i, far);
    split(far, j);
  };
  if (n >= 2 && dist3(pts[0], pts[n]) < 0.5) {
    let m = 1;
    for (let k = 2; k < n; k++) if (dist3(pts[k], pts[0]) > dist3(pts[m], pts[0])) m = k;
    keep.add(m);
    split(0, m);
    split(m, n);
  } else split(0, n);
  return [...keep].sort((a, b) => a - b);
}

// ---- Light levels ---------------------------------------------------------------------

/** A light as the light levels see it (room local, mm over the floor): a point
 *  shining along `aim` (unit) with `i0` candela there, falling off as cos^m of
 *  the angle from it. */
export interface LuxSource { at: Vec3; aim: Vec3; i0: number; m: number }

/** A beam's lobe: the cos^m exponent that halves its intensity at half its beam
 *  angle (°) — 120° gives 1, a Lambertian —, and its peak for `lm` lumens
 *  (2π·i0 / (m + 1) of them leave it). */
export function beamLobe(beam: number, lm: number): { i0: number; m: number } {
  const half = (Math.min(89, Math.max(1, beam / 2)) * Math.PI) / 180;
  const m = Math.log(0.5) / Math.log(Math.cos(half));
  return { m, i0: (lm * (m + 1)) / (2 * Math.PI) };
}

/** The longest piece of a line light that counts as one point (mm). */
const LUX_PIECE = 100;

/** A room's lights as light sources: each spot from its lens; each straight
 *  stretch of a strip, of the cove's strip and of a channel in pieces of at most
 *  `LUX_PIECE`, each its share of its product's lumens per metre. Each opens as
 *  its emitter's beam (`lightBeam`, under the drawing's `cones`); a fibre end is
 *  a pinpoint and gives none. */
export function luxSources(inst: ProductInstance, cones: BeamCones = BEAM_DEFAULTS): LuxSource[] {
  const byId = new Map(lightsOf(inst).map((l) => [l.id, l]));
  const unit = (v: Vec3): Vec3 => { const n = Math.hypot(v[0], v[1], v[2]) || 1; return [v[0] / n, v[1] / n, v[2] / n]; };
  const out: LuxSource[] = [];
  for (const e of lightEmitters(inst, cones)) {
    const light = byId.get(e.id);
    if (!light || e.kind === 'fibre') continue;
    const lm = lightProduct(light)?.lightLumens ?? 0;
    if (!(lm > 0)) continue;
    if (e.kind === 'spot') { out.push({ at: e.at, aim: unit(e.aim), ...beamLobe(e.beam.angle, lm) }); continue; }
    const len = Math.hypot(e.b[0] - e.a[0], e.b[1] - e.a[1], e.b[2] - e.a[2]), n = Math.max(1, Math.ceil(len / LUX_PIECE));
    const lobe = beamLobe(e.beam.angle, (lm * len) / 1000 / n);
    for (let k = 0; k < n; k++) {
      const t = (k + 0.5) / n;
      out.push({ at: [e.a[0] + (e.b[0] - e.a[0]) * t, e.a[1] + (e.b[1] - e.a[1]) * t, e.a[2] + (e.b[2] - e.a[2]) * t], aim: unit(e.aim), ...lobe });
    }
  }
  return out;
}

/** The illuminance (lux) on a level surface facing up at `p` (room local, mm over
 *  the floor): each source over it, i0·cos^m(off its aim)·cos(incidence) / d² —
 *  no shadows, no light reflected off the walls. */
export function illuminanceAt(sources: readonly LuxSource[], p: Vec3): number {
  let e = 0;
  for (const s of sources) {
    const dx = p[0] - s.at[0], dy = p[1] - s.at[1], dz = p[2] - s.at[2];
    if (dz >= 0) continue; // under the surface it would light
    const d2 = dx * dx + dy * dy + dz * dz, d = Math.sqrt(d2);
    const off = (dx * s.aim[0] + dy * s.aim[1] + dz * s.aim[2]) / d;
    if (off <= 0) continue;
    e += (s.i0 * Math.pow(off, s.m) * (-dz / d)) / (d2 / 1e6);
  }
  return e;
}

export interface LuxStats { min: number; avg: number; max: number }
/** A room's light levels: on a grid of `cell` mm over its floor, each cell's
 *  illuminance on what it meets first from above — a bench's seat, else the
 *  floor —, and the floor's and the benches' least, mean and most. Null: no
 *  light gives any. */
export interface LightLevels {
  cell: number;
  cells: Array<{ x: number; y: number; z: number; lux: number; bench: boolean }>;
  floor: LuxStats | null;
  benches: LuxStats | null;
}

/** The most cells a room's light levels take: a larger room takes larger cells. */
const LUX_CELLS = 12000;

/** A room's light levels (`LightLevels`), resolved once per room
 *  (`resolvedLighting`) for each cell size and beam sizes. */
export function lightLevels(inst: ProductInstance, cell = 100, cones: BeamCones = BEAM_DEFAULTS): LightLevels | null {
  if (!lightsOf(inst).length) return null;
  const r = resolvedLighting(inst), k = JSON.stringify([cell, cones]);
  if (!r.levels.has(k)) r.levels.set(k, levelsOf(inst, cell, cones));
  return r.levels.get(k) ?? null;
}

function levelsOf(inst: ProductInstance, cell: number, cones: BeamCones): LightLevels | null {
  const sources = luxSources(inst, cones);
  const ring = polyInnerCarved(inst);
  if (!sources.length || ring.length < 3) return null;
  const xs = ring.map((p) => p.x), ys = ring.map((p) => p.y);
  const x0 = Math.min(...xs), x1 = Math.max(...xs), y0 = Math.min(...ys), y1 = Math.max(...ys);
  const c = Math.max(cell, Math.sqrt(((x1 - x0) * (y1 - y0)) / LUX_CELLS));
  const seats = roomBenchLines(inst).map((l) => ({ poly: [...l.path(0), ...l.path(l.depth).reverse()], top: l.top }));
  const cells: LightLevels['cells'] = [];
  for (let x = x0 + c / 2; x < x1; x += c) {
    for (let y = y0 + c / 2; y < y1; y += c) {
      const p = { x, y };
      if (!pointInPolygon(p, ring)) continue;
      let z = 0, bench = false;
      for (const s of seats) if (s.top > z && pointInPolygon(p, s.poly)) { z = s.top; bench = true; }
      cells.push({ x, y, z, lux: illuminanceAt(sources, [x, y, z]), bench });
    }
  }
  const stats = (on: boolean): LuxStats | null => {
    const v = cells.filter((q) => q.bench === on).map((q) => q.lux);
    return v.length ? { min: Math.min(...v), avg: v.reduce((a, b) => a + b, 0) / v.length, max: Math.max(...v) } : null;
  };
  return { cell: c, cells, floor: stats(false), benches: stats(true) };
}

// ---- The lights as a drawn plan has them --------------------------------------------

/** A sky's fibre end on a drawn plan (an RCP, the DXF): a small ring (radius, mm). */
export const FIBRE_MARK = 8;

/** One line of a room's lights (room local). `ring`: the circle it traces (a
 *  spot's, a fibre end's), for a writer with circles; `ceiling`: a cove's second
 *  layer or ledge, in the ceiling's ink; `zone`: a sky's zone; `hidden`: over the
 *  ceiling, out of sight — a fibre kit's projector and its arcs. */
export interface LightLine {
  pts: Vec2[];
  closed: boolean;
  id?: string;
  /** A backrest's up or down strip: drawn `STRIP_APART_PX` off the other
   *  (`StripRun.apart`), toward `side` (`drawnStripPts`). */
  apart?: { by: 1 | -1; side: 1 | -1 };
  ring?: { c: Vec2; r: number };
  ceiling?: boolean;
  zone?: boolean;
  hidden?: boolean;
}

/** A room's lights as lines, in paint order: its strips (those `strip` keeps by
 *  height), a cove's parts and strip, each channel's two sides, a spot's ring and
 *  cross, a fibre point's ring, a sky's zone and fibre rings, each fibre kit's
 *  projector and arcs (its part found in `parts`). */
export function lightLines(room: ProductInstance, parts?: ReadonlyMap<string, ProductInstance>, strip: (z: number) => boolean = () => true): LightLine[] {
  const out: LightLine[] = [];
  const line = (l: LightLine): void => { if (l.pts.length >= 2) out.push(l); };
  for (const piece of stripPieces(room)) if (strip(piece.run.z)) line({ pts: piece.pts, closed: false, id: piece.id, ...(piece.run.apart ? { apart: { by: piece.run.apart, side: piece.run.side } } : {}) });
  const cove = coveOf(room), cg = cove ? coveGeometry(room) : null;
  if (cove && cg) {
    for (const part of cg.parts) line({ pts: part.poly, closed: true, ceiling: true });
    for (const run of cg.runs) line({ pts: run.pts, closed: run.closed, id: cove.id });
  }
  for (const ch of channelsOf(room)) {
    const sides = pathSides(ch.pts, channelFigures(ch).width);
    if (sides) for (const side of sides) line({ pts: side, closed: false, id: ch.id });
  }
  for (const sp of spotsOf(room)) {
    const r = (sp.fibre ? FIBRE_DIAMETER : SPOT_DIAMETER) / 2, { x, y } = sp.at;
    line({ pts: circleRing(sp.at, r), closed: true, id: sp.id, ring: { c: sp.at, r } });
    if (sp.fibre) continue;
    const q = r * 0.72;
    line({ pts: [{ x: x - q, y: y - q }, { x: x + q, y: y + q }], closed: false, id: sp.id });
    line({ pts: [{ x: x + q, y: y - q }, { x: x - q, y: y + q }], closed: false, id: sp.id });
  }
  for (const sky of skiesOf(room)) {
    line({ pts: skyZone(room, sky), closed: true, id: sky.id, zone: true });
    for (const f of skyFibres(room, sky)) line({ pts: circleRing(f.at, FIBRE_MARK, 8), closed: true, id: sky.id, ring: { c: f.at, r: FIBRE_MARK } });
  }
  for (const kit of fibreKitsOf(room)) {
    const part = kit.projectorId ? parts?.get(kit.projectorId) : undefined;
    if (!part) continue;
    line({ pts: partFootprintIn(room, part), closed: true, id: kit.id, hidden: true });
    for (const arc of kitArcs(room, projectorMiddle(room, part), kitFibres(room, kit))) line({ pts: arc.map(([x, y]) => ({ x, y })), closed: false, id: kit.id, hidden: true });
  }
  return out;
}

// ---- Products and power -------------------------------------------------------------

/** The product a light names, else the built-in generic for its kind and colour. */
/** Where the lights find their products: the catalogue main.ts hands them
 *  (`setLightCatalogue`: the user's products first), else — or handed null —
 *  the built-in generics. */
const builtinLight = (id: string): ProductTemplate | undefined => BUILTIN_LIGHTING.find((t) => t.id === id);
let lightCatalogue: (id: string) => ProductTemplate | undefined = builtinLight;
export function setLightCatalogue(fn: ((id: string) => ProductTemplate | undefined) | null): void {
  lightCatalogue = fn ?? builtinLight;
}
/** A lighting product by id, from the catalogue. */
export function catalogueLightProduct(id: string): ProductTemplate | undefined {
  return lightCatalogue(id);
}

/** Give a light its catalogue product, or none: its id and its names, kept so
 *  the schedule reads them offline. */
export function setLightProduct(l: RoomLight, t: ProductTemplate | undefined): void {
  delete l.productId; delete l.productName; delete l.productNameI18n;
  if (!t) return;
  l.productId = t.id;
  l.productName = t.name;
  if (t.nameI18n) l.productNameI18n = { ...t.nameI18n };
}

/** A light's product name in `lang`: `t`'s (its product, when the catalogue
 *  has it), else the names kept with the light; '' with none. */
export function lightProductName(l: RoomLight, t: ProductTemplate | undefined, lang: Lang): string {
  if (t) return productName(t, lang);
  return l.productName ? productName({ name: l.productName, nameI18n: l.productNameI18n }, lang) : '';
}

/** How wide a light's beam opens (°): its product's beam angle, else `fallback`
 *  (the drawing's size for its type). */
export function lightBeamAngle(light: RoomLight, fallback: number): number {
  const a = lightProduct(light, lightCatalogue)?.lightBeamAngle;
  return a != null && Number.isFinite(a) && a > 0 && a < 180 ? a : fallback;
}

export function lightProduct(light: RoomLight, productOf: (id: string) => ProductTemplate | undefined = lightCatalogue): ProductTemplate | undefined {
  return (light.productId ? productOf(light.productId) : undefined) ?? productOf(defaultLightProductId(light));
}

/** The built-in generic product id a light of its kind and colour takes. */
export function defaultLightProductId(light: Pick<RoomLight, 'kind' | 'colour'> & { fibre?: boolean }): string {
  const rgb = light.colour === 'RGB' ? '-rgb' : '';
  if (light.kind === 'spot' && !light.fibre) return `builtin-spot${rgb}`;
  if (light.kind === 'sky' || light.kind === 'spot') return `builtin-fibre-projector${rgb}`;
  return `builtin-led-strip${rgb}`;
}

// ---- Tags ---------------------------------------------------------------------------

/** What a light is in the Lighting table, and so its tag: its product in its place
 *  — a strip's position, a spot or a fibre point, a sky's kit (its product is its
 *  projector's), the cove, a channel. */
export function lightTagKey(light: RoomLight): string {
  const pid = light.productId ?? defaultLightProductId(light);
  switch (light.kind) {
    case 'strip': return `strip:${light.pos}|${pid}`;
    case 'spot': return `${light.fibre ? 'fibre' : 'spot'}|${pid}`;
    case 'sky': return `sky:${skyCount(light)}|${pid}`;
    case 'cove': return `cove|${pid}`;
    case 'channel': return `channel|${pid}`;
  }
}

/** The tag keys a drawing's lights take, each once, in the order they first
 *  appear (rooms in drawing order, each room's lights in its order). */
export function lightKeysInOrder(instances: readonly ProductInstance[]): string[] {
  const seen = new Set<string>();
  for (const inst of instances) for (const l of inst.lights ?? []) seen.add(lightTagKey(l));
  return [...seen];
}

/** The kept tag order brought up to date: a key no light takes any more drops out
 *  (the tags after it close up), a new one goes last — so adding a light never
 *  renumbers the others. */
export function updatedTagOrder(kept: readonly string[] | undefined, present: readonly string[]): string[] {
  const now = new Set(present), out = (kept ?? []).filter((k) => now.has(k));
  const had = new Set(out);
  for (const k of present) if (!had.has(k)) { out.push(k); had.add(k); }
  return out;
}

/** The point halfway along a path and the direction there (room local). */
export function pathMidpoint(pts: readonly Vec2[]): { p: Vec2; dir: Vec2 } | null {
  let left = pathLengthOf(pts) / 2;
  for (let i = 0; i + 1 < pts.length; i++) {
    const a = pts[i], b = pts[i + 1], len = dist(a, b);
    if (len > 1e-9 && left <= len) return { p: lerpPt(a, b, left / len), dir: { x: (b.x - a.x) / len, y: (b.y - a.y) / len } };
    left -= len;
  }
  return null;
}

/** Where a room's tags go (room local): one per strip run — beside its middle,
 *  `side` the way it leans into the room —, one beside each spot and fibre
 *  point, one at the middle of each channel and of the cove's longest run, one in
 *  a sky's zone near its first corner. `id` is the light that keeps the tag's
 *  `tagOffset` (a run's first strip), `offset` that offset when it was moved. */
export interface TagAnchor { key: string; id: string; at: Vec2; side?: Vec2; offset?: Vec2; /** A strip's height (mm). */ z?: number }

export function lightTagAnchors(inst: ProductInstance): TagAnchor[] {
  const out: TagAnchor[] = [];
  const byId = new Map(lightsOf(inst).map((l) => [l.id, l]));
  const tag = (l: RoomLight, a: Omit<TagAnchor, 'key' | 'id' | 'offset'>): void => {
    out.push({ key: lightTagKey(l), id: l.id, ...a, ...(l.tagOffset ? { offset: { ...l.tagOffset } } : {}) });
  };
  for (const run of stripRuns(inst)) {
    const m = pathMidpoint(run.pts), first = byId.get(run.ids[0]);
    if (m && first) tag(first, { at: m.p, side: { x: -m.dir.y * run.side, y: m.dir.x * run.side }, z: run.z });
  }
  for (const s of spotsOf(inst)) tag(s, { at: s.at });
  for (const ch of channelsOf(inst)) {
    const m = pathMidpoint(ch.pts);
    if (m) tag(ch, { at: m.p, side: { x: -m.dir.y, y: m.dir.x } });
  }
  const cove = coveOf(inst), runs = cove ? coveGeometry(inst)?.runs ?? [] : [];
  const longest = runs.reduce<(typeof runs)[number] | null>((m, r) => (!m || r.length > m.length ? r : m), null);
  const cm = longest ? pathMidpoint(longest.pts) : null;
  if (cove && cm && longest) tag(cove, { at: cm.p, side: { x: -cm.dir.y * longest.side, y: cm.dir.x * longest.side } });
  for (const sky of skiesOf(inst)) {
    const zone = skyZone(inst, sky);
    if (zone.length < 3) continue;
    const c = polygonCentroid(zone), k = Math.min(1, 250 / (dist(zone[0], c) || 1));
    tag(sky, { at: lerpPt(zone[0], c, k) });
  }
  return out;
}

/** What a light is drawn as on the plan — the symbol its tag's legend shows. */
export type LightSymbol =
  | 'channel'
  | 'cove'
  | 'fibre'
  | 'sky'
  | 'spot'
  | 'strip-down'
  | 'strip-up';

export function lightSymbolOf(l: RoomLight): LightSymbol {
  switch (l.kind) {
    case 'strip': return STRIP_FACING[l.pos] === 'up' ? 'strip-up' : 'strip-down';
    case 'spot': return l.fibre ? 'fibre' : 'spot';
    case 'channel': return 'channel';
    case 'cove': return 'cove';
    case 'sky': return 'sky';
    default: return unreachable(l, 'light kind');
  }
}

/** A stroke of a legend symbol: a polyline (`dashed`, `closed`, a closed one
 *  `fill`ed) or a ring (`fill`ed: a dot). */
export interface SymbolStroke { pts?: Vec2[]; ring?: { c: Vec2; r: number }; closed?: boolean; dashed?: boolean; fill?: boolean }

/** A legend symbol in a `w` × `h` box about the origin (y up), as the plan draws
 *  that light: a spot's ring and cross, a fibre point's ring and dot, a sky's
 *  dashed zone and fibres, a strip's dashed line with ▼ or ▲, a channel's two
 *  dashed sides, a cove's strip under the ceiling's edge. */
export function lightSymbolStrokes(sym: LightSymbol, w: number, h: number): SymbolStroke[] {
  const m = Math.min(w, h) / 2, x = w * 0.45;
  const line = (y: number, dashed: boolean): SymbolStroke => ({ pts: [{ x: -x, y }, { x, y }], dashed });
  switch (sym) {
    case 'spot': {
      const r = m * 0.8, q = r * 0.72;
      return [{ ring: { c: { x: 0, y: 0 }, r } }, { pts: [{ x: -q, y: -q }, { x: q, y: q }] }, { pts: [{ x: q, y: -q }, { x: -q, y: q }] }];
    }
    case 'fibre': return [{ ring: { c: { x: 0, y: 0 }, r: m * 0.4 } }, { ring: { c: { x: 0, y: 0 }, r: m * 0.12 }, fill: true }];
    case 'sky': {
      const zx = x, zy = m * 0.8;
      const dots = [[-0.6, 0.4], [-0.15, -0.35], [0.3, 0.45], [0.65, -0.25], [-0.55, -0.45]].map(([a, b]) => ({ ring: { c: { x: a * zx, y: b * zy }, r: m * 0.09 }, fill: true }));
      return [{ pts: [{ x: -zx, y: -zy }, { x: zx, y: -zy }, { x: zx, y: zy }, { x: -zx, y: zy }], closed: true, dashed: true }, ...dots];
    }
    case 'strip-down':
    case 'strip-up': {
      const up = sym === 'strip-up', t = m * 0.3, y0 = -m * 0.35;
      const tri = up ? [{ x: -t, y: m * 0.15 }, { x: t, y: m * 0.15 }, { x: 0, y: m * 0.75 }] : [{ x: -t, y: m * 0.75 }, { x: t, y: m * 0.75 }, { x: 0, y: m * 0.15 }];
      return [line(y0, true), { pts: tri, closed: true, fill: true }];
    }
    case 'channel': return [line(m * 0.3, true), line(-m * 0.3, true)];
    case 'cove': return [line(m * 0.45, false), line(-m * 0.2, true)];
    default: return unreachable(sym, 'light symbol');
  }
}

/** A lighting legend as DXF entities (world mm, on `LIGHTS`), its top left at
 *  `at`: the title, then a row per tag — its symbol (`lightSymbolStrokes`, a dot
 *  or a ▼ filled), its tag, what it is —, `th` the text height. */
export function lightingLegendDxf(rows: ReadonlyArray<{ tag: string; symbol: LightSymbol; text: string }>, at: Vec2, th: number, title: string): DxfEntity[] {
  const out: DxfEntity[] = [{ kind: 'text', pos: { ...at }, height: th * 1.2, text: title, baseline: 'top', layer: 'LIGHTS' }];
  rows.forEach((r, k) => {
    const cy = at.y - th * 3 - k * th * 2.6, c = { x: at.x + th * 2.5, y: cy };
    const P = (p: Vec2): Vec2 => ({ x: c.x + p.x, y: c.y + p.y });
    for (const st of lightSymbolStrokes(r.symbol, th * 4, th * 2)) {
      const ltype = st.dashed ? { ltype: 'DASHED' as const } : {};
      if (st.ring) {
        const rc = P(st.ring.c);
        out.push({ kind: 'circle', c: rc, r: st.ring.r, layer: 'LIGHTS', color: LIGHT_ACI });
        if (st.fill) out.push({ kind: 'hatch', loops: [circleRing(rc, st.ring.r, 12)], layer: 'LIGHTS', color: LIGHT_ACI });
      } else if (st.pts) {
        const pts = st.pts.map(P);
        out.push(pts.length === 2 && !st.closed
          ? { kind: 'line', a: pts[0], b: pts[1], layer: 'LIGHTS', color: LIGHT_ACI, ...ltype }
          : { kind: 'polyline', points: pts, closed: !!st.closed, layer: 'LIGHTS', color: LIGHT_ACI, ...ltype });
        if (st.fill && st.closed) out.push({ kind: 'hatch', loops: [pts], layer: 'LIGHTS', color: LIGHT_ACI });
      }
    }
    out.push({ kind: 'text', pos: { x: at.x + th * 5.2, y: cy }, height: th, text: r.tag, baseline: 'middle', layer: 'LIGHTS', color: LIGHT_ACI });
    out.push({ kind: 'text', pos: { x: at.x + th * 7.6, y: cy }, height: th, text: r.text, baseline: 'middle', layer: 'LIGHTS' });
  });
  return out;
}

/** Each tag key's tag, L1, L2 … in the kept order. */
export function lightTags(order: readonly string[]): Map<string, string> {
  return new Map(order.map((k, i) => [k, `L${i + 1}`]));
}

/** Where a tag's text goes on a drawn plan (a DXF's, room local): where it was
 *  moved to, else 160 mm into the room from a strip's or a channel's middle, else
 *  up and right of its light. */
export function tagTextAt(a: TagAnchor): Vec2 {
  if (a.offset) return { x: a.at.x + a.offset.x, y: a.at.y + a.offset.y };
  return a.side ? { x: a.at.x + a.side.x * 160, y: a.at.y + a.side.y * 160 } : { x: a.at.x + 120, y: a.at.y + 120 };
}

/** How far short of a moved tag's text (100 mm high) its leader stops (mm). */
export const TAG_LEADER_GAP = 110;

/** A moved tag's leader on a drawn plan (room local): from its light to
 *  `TAG_LEADER_GAP` short of its text — none when the tag was never moved or
 *  stands too near its light to need one. */
export function tagLeader(a: TagAnchor): [Vec2, Vec2] | null {
  if (!a.offset) return null;
  const t = tagTextAt(a), d = dist(a.at, t);
  if (d < TAG_LEADER_GAP + 40) return null;
  return [a.at, lerpPt(a.at, t, (d - TAG_LEADER_GAP) / d)];
}

/** Whether a light is bought by the metre — a strip, the cove, a channel — rather
 *  than by the piece (a spot, a fibre kit). */
export function lightByTheMetre(light: RoomLight): boolean {
  return light.kind === 'strip' || light.kind === 'cove' || light.kind === 'channel';
}

/** How long a light is (mm): a strip its own part of its run, the cove its
 *  strip's runs, a channel its path; a spot or a sky none. */
export function lightLength(room: ProductInstance, light: RoomLight): number {
  switch (light.kind) {
    case 'strip': { const piece = stripPieces(room).find((p) => p.id === light.id); return piece ? pathLength(piece.pts) : 0; }
    case 'cove': return (coveGeometry(room)?.runs ?? []).reduce((m, r) => m + r.length, 0);
    case 'channel': return pathLengthOf(light.pts);
    case 'spot': case 'sky': return 0;
    default: return unreachable(light, 'light kind');
  }
}

/** A light's power (W), the ONE formula its panel and the Lighting table read:
 *  `amount` mm of a strip, the cove or a channel at its product's W/m, else
 *  `amount` pieces of a spot or a fibre kit at its product's watts (a kit's
 *  product is its projector). */
export function lightWatts(light: RoomLight, amount: number): number {
  const p = lightProduct(light);
  return lightByTheMetre(light) ? (amount / 1000) * (p?.powerPerMeter ?? 0) : amount * (p?.lightPower ?? 0);
}

// ---- The Strip tool -----------------------------------------------------------------

/** A place the Strip tool can put a strip: its host, position and the path it
 *  would run along (room local), for the green / red preview. */
export interface StripTarget { host: StripHost; pos: StripPos; pts: Vec2[]; existing?: string }

/** The strip target under `p` (room local): a bench band or a backrest the
 *  point is over. On a bench, `pos` if given (else A1 nearer its front, B nearer
 *  its back); on a backrest, C (or D if given). A strip already there is `existing`.
 *  `accept` keeps to the hosts it takes — in 3D, what the click is on: a backrest
 *  stands over its bench, and the tiers overlap, where a plan point is both. */
export function stripTargetAt(inst: ProductInstance, p: Vec2, pos: StripPos | null, lines: BenchLine[] = roomBenchLines(inst), tol = 0, facing?: 'up' | 'down', accept?: (host: StripHost) => boolean): StripTarget | null {
  const strips = stripsOf(inst);
  const found = (host: StripHost, at: StripPos, pts: Vec2[]): StripTarget => {
    const ex = strips.find((s) => s.pos === at && ('bench' in host ? 'bench' in s.host && s.host.bench === host.bench : 'backrest' in s.host && s.host.backrest === host.backrest));
    return { host, pos: at, pts, ...(ex ? { existing: ex.id } : {}) };
  };
  for (const br of inst.backrests ?? []) {
    if (accept && !accept({ backrest: br.id })) continue;
    const run = backrestRun(inst, br);
    if (!run) continue;
    const f = backrestFigures(br);
    const pt = (k: 0 | 1, d: number): Vec2 => facePoint(run.face, endStation(run.face, run.ends[k], d), d);
    const deep = f.standOff + f.boardThickness + tol;
    if (!pointInPolygon(p, [pt(0, 0), pt(1, 0), pt(1, deep), pt(0, deep)])) continue;
    const at: StripPos = pos === 'C' || pos === 'D' ? pos : facing === 'down' ? 'D' : 'C';
    const place = backrestStripPlace(inst, br, at);
    if (!place) continue;
    return found({ backrest: br.id }, at, [pt(0, place.d), pt(1, place.d)]);
  }
  for (const line of [...lines].reverse()) {
    if (line.kind !== 'band' || (accept && !accept({ bench: line.id }))) continue;
    const back = line.path(0), front = line.path(line.depth);
    if (back.length < 2 || front.length < 2) continue;
    const quad = [back[0], back[back.length - 1], front[front.length - 1], front[0]];
    if (!pointInPolygon(p, quad)) continue;
    // Nearer the front: A1; nearer the back: B — unless a position is asked for,
    // or one way: down A1, up B.
    const dBack = Math.abs((p.x - back[0].x) * (front[0].x - back[0].x) + (p.y - back[0].y) * (front[0].y - back[0].y)) / (dist(back[0], front[0]) || 1);
    const at: StripPos = pos ?? (facing === 'down' ? 'A1' : facing === 'up' ? 'B' : dBack < line.depth / 3 ? 'B' : 'A1');
    const place = benchStripPlace(line, at);
    if (!place) return null;
    return found({ bench: line.id }, at, line.path(place.d));
  }
  return null;
}

/** The strips whose host is gone — a bench or a backrest deleted — or whose
 *  position the host no longer offers (a steam bench re-profiled), which go too. */
export function orphanStrips(inst: ProductInstance, given?: BenchLine[]): string[] {
  const strips = stripsOf(inst);
  if (!strips.length) return [];
  const lines = given ?? roomBenchLines(inst);
  return strips.filter((s) => !stripLine(inst, s, lines)).map((s) => s.id);
}

/** The lights that have lost what they are fixed to, and go the frame after the
 *  edit (main.ts `reconcileFixtures`): an orphaned strip (`orphanStrips`), a cove
 *  left on no wall. A spot, a sky and a channel are the ceiling's. */
export function staleLights(inst: ProductInstance): string[] {
  const strips = new Set(orphanStrips(inst));
  return lightsOf(inst).filter((l) => {
    switch (l.kind) {
      case 'strip': return strips.has(l.id);
      case 'cove': return !l.walls.length;
      case 'spot': case 'sky': case 'channel': return false;
      default: return unreachable(l, 'light kind');
    }
  }).map((l) => l.id);
}

/**
 * A room's lights adapted to another room (`to`, its own local frame), each with
 * a new id, and those that find no place there. A point — a spot, a channel's
 * path, a drawn sky zone's corners — keeps its distance from the NEARER side of
 * the room's inner outline on each axis (on a centre line, the other room's), and
 * must land inside it. A strip goes to the bench (the same tier on its wall) or
 * the backrest of the wall that faces the same way and lies where its own does,
 * at a position that host offers; a cove to the walls that match its own, unless
 * the room has one; a whole-ceiling sky as it is. A fibre kit's projector, a tag
 * moved by hand and fibres dragged by hand stay behind.
 */
export function adaptLights(from: ProductInstance, to: ProductInstance, newId: () => string): { lights: RoomLight[]; skipped: RoomLight[] } {
  const A = polyInnerCarved(from), B = polyInnerCarved(to);
  const box = (r: Vec2[]): { x0: number; x1: number; y0: number; y1: number } => ({
    x0: Math.min(...r.map((p) => p.x)), x1: Math.max(...r.map((p) => p.x)), y0: Math.min(...r.map((p) => p.y)), y1: Math.max(...r.map((p) => p.y)),
  });
  const a = box(A), b = box(B);
  const along = (v: number, lo: number, hi: number, lo2: number, hi2: number): number => {
    const dl = v - lo, dh = hi - v;
    return Math.abs(dl - dh) <= 2 ? (lo2 + hi2) / 2 : dl < dh ? lo2 + dl : hi2 - dh;
  };
  const map = (p: Vec2): Vec2 => ({ x: Math.round(along(p.x, a.x0, a.x1, b.x0, b.x1)), y: Math.round(along(p.y, a.y0, a.y1, b.y0, b.y1)) });
  const inside = (pts: Vec2[]): boolean => pts.every((p) => pointInPolygon(p, B));
  /** The edge of `to` matching edge `i` of `from`: the same way round, nearest
   *  where its middle maps to. */
  const match = (ef: PolyEdge[], et: PolyEdge[], i: number): number | null => {
    const e = ef[i];
    if (!e) return null;
    const m = map({ x: (e.ia.x + e.ib.x) / 2, y: (e.ia.y + e.ib.y) / 2 });
    let best: number | null = null, bd = Infinity;
    et.forEach((f, j) => {
      if (e.dir.x * f.dir.x + e.dir.y * f.dir.y < 0.999) return;
      const d = dist(m, { x: (f.ia.x + f.ib.x) / 2, y: (f.ia.y + f.ib.y) / 2 });
      if (d < bd) { bd = d; best = j; }
    });
    return best;
  };
  const benchEdges = [polyEdges(from), polyEdges(to)] as const, wallEdges = [polyEdges(wallView(from)), polyEdges(wallView(to))] as const;
  const lights: RoomLight[] = [], skipped: RoomLight[] = [];
  let cove = !!coveOf(to);
  for (const l of from.lights ?? []) {
    const { tagOffset: _t, ...rest } = l;
    const base = { ...rest, id: newId() };
    const out = ((): RoomLight | null => {
      switch (l.kind) {
        case 'spot': {
          const at = map(l.at);
          const { projectorId: _p, ...spot } = base as typeof l;
          return inside([at]) ? { ...spot, at } : null;
        }
        case 'channel': {
          if (!l.anchor) { const pts = l.pts.map(map); return inside(pts) ? { ...(base as typeof l), pts } : null; }
          // Laid along walls: along the matching walls there, as far in.
          const walls = l.anchor.closed ? [] : l.anchor.walls.map((w) => match(wallEdges[0], wallEdges[1], w)).filter((w): w is number => w != null);
          const anchor = { ...l.anchor, walls }, pts = l.anchor.closed || walls.length ? channelAnchorPath(to, anchor) : null;
          return pts ? { ...(base as typeof l), anchor, pts: pts.map((q) => ({ x: Math.round(q.x), y: Math.round(q.y) })) } : null;
        }
        case 'sky': {
          const { projectorId: _p, moved: _m, ...sky } = base as typeof l;
          const zone = l.zone?.map(map);
          return !zone || inside(zone) ? { ...sky, ...(zone ? { zone } : {}) } : null;
        }
        case 'cove': {
          const walls = l.walls.map((w) => match(wallEdges[0], wallEdges[1], w)).filter((w): w is number => w != null);
          if (cove || !walls.length) return null;
          cove = true;
          return { ...(base as typeof l), walls: [...new Set(walls)] };
        }
        case 'strip': {
          let host: StripHost | null = null;
          if ('bench' in l.host) {
            const id = l.host.bench, src = (from.polyBenches ?? []).find((x) => x.id === id);
            const e = src ? match(benchEdges[0], benchEdges[1], src.edge) : null;
            const tier = src ? (from.polyBenches ?? []).filter((x) => x.edge === src.edge).indexOf(src) : -1;
            const dst = e == null ? undefined : (to.polyBenches ?? []).filter((x) => x.edge === e)[tier];
            if (dst) host = { bench: dst.id };
          } else {
            const id = l.host.backrest, src = (from.backrests ?? []).find((x) => x.id === id);
            const w = src && 'wall' in src.host ? match(wallEdges[0], wallEdges[1], src.host.wall) : null;
            const dst = w == null ? undefined : (to.backrests ?? []).find((x) => 'wall' in x.host && x.host.wall === w);
            if (dst) host = { backrest: dst.id };
          }
          return host && hostPositions(to, host).includes(l.pos) ? { ...(base as typeof l), host } : null;
        }
        default: return unreachable(l, 'light kind');
      }
    })();
    if (out) lights.push(out); else skipped.push(l);
  }
  return { lights, skipped };
}

